// src/AppShell.jsx
//
// ROUTING-PHASE-1: Main app shell using React Router v6.
// Extracted from App.jsx — contains all state management + Routes tree.
// App.jsx is now a thin BrowserRouter wrapper that re-exports the pure
// functions; AppShell owns all navigational and auth state.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { setWeeklyReadIdentity } from "./services/projectWeeklyReads.js";
import { APP_NAME } from "./constants/branding.js";
import {
  Routes, Route, Navigate, useNavigate, useLocation, useMatch, useSearchParams,
} from "react-router-dom";

import { PortfolioView } from "./features/locations/PortfolioView.jsx";
import { LocationDetail } from "./features/locations/LocationDetail.jsx";
import { AddLocationModal } from "./features/locations/AddLocationModal.jsx";
import { getLocations } from "./data/locationsClient.js";
import { rankSites } from "./utils/rankSites.js";
import {
  loadPortfolioState, DEFAULT_PORTFOLIO_STATE,
  loadLeasesFilters, loadLeasesSort,
  saveLastListView, saveCurrentNavLease, saveCurrentNavLeaseLocation,
  clearCurrentNavLease, loadLastListView, loadCurrentNavLease, loadCurrentNavLeaseLocation,
} from "./services/portfolioStatePersistence.js";
import { applyAllFilters, applyAllFiltersAndSort, applySearchFilter } from "./services/filterLocations.js";
import { applyColumnSort } from "./utils/sortLocationsColumn.js";
import { applyColumnSort as applyLeaseColumnSort } from "./utils/sortLeasesColumn.js";
import { applyLeaseSort } from "./features/leases/leasePortfolioSort.js";
import { applyFilters as applyLeaseFilters, DEFAULT_FILTERS as DEFAULT_LEASE_FILTERS } from "./features/leases/pages/components/LeasePortfolioFilters.jsx";
import { getPortfolioLeases, patchPortfolioLeasesCache } from "./data/leasesPortfolioClient.js";
import { getPlan } from "./data/marketsClient.js";
import { getCommandIndex } from "./data/commandIndexClient.js";
import { subscribePlanInvalidation, unsubscribePlanInvalidation } from "./services/cacheBus.js";
import {
  ReportsPage,
  REPORT_METADATA,
  REPORT_TILE_TITLES,
  getOrderedReportKeys,
} from "./features/reports/ReportsPage.jsx";
import { WeeklyPortfolioReview } from "./features/reports/WeeklyPortfolioReview.jsx";
import { HeadcountTrendReport } from "./features/reports/HeadcountTrendReport.jsx";
import { OpenRolesReport } from "./features/reports/OpenRolesReport.jsx";
import { SpaceFitReport } from "./features/reports/SpaceFitReport.jsx";
import { CostLeakageReport } from "./features/reports/CostLeakageReport.jsx";
import { RtoComplianceReport } from "./features/reports/RtoComplianceReport.jsx";
import { FootTrafficReport } from "./features/reports/FootTrafficReport.jsx";
import { UtilizationReport } from "./features/reports/UtilizationReport.jsx";
import { DataCoverageReport } from "./features/reports/DataCoverageReport.jsx";
import { ReportErrorBoundary } from "./components/ReportErrorBoundary.jsx";
import { AdminPage, ADMIN_ACTIVE_SECTION_STORAGE_KEY } from "./features/admin/AdminPage.jsx";
import { CurrencyRatesPage } from "./features/admin/CurrencyRatesPage.jsx";
import { RoadmapPage } from "./features/roadmap/RoadmapPage.jsx";
import { WorkflowsPage } from "./features/workflows/WorkflowsPage.jsx";
import { ClosureRunPage } from "./features/workflows/ClosureRunPage.jsx";
import { getCurrentUser, subscribeCurrentUser } from "./data/operationsClient.js";
import {
  clearClientRolePreview,
  getClientRolePreview,
  setClientRolePreview,
} from "./utils/demoRolePreview.js";
import { AuthError } from "./data/errors.js";
import { getDevActAsStatus } from "./data/devActAsClient.js";
import { T, STAGES } from "./app/tokens.js";
import { useInteractionLayer } from "./app/useInteractionLayer.js";
import { log } from "./utils/logger.js";
import { readNavParams } from "./services/urlNav.js";
import { useFeatureFlags } from "./config/featureFlags.js";
import { useFeatureFlagContext } from "./contexts/FeatureFlagContext.jsx";
import { PendingImportsQueue } from "./features/imports/PendingImportsQueue.jsx";
import { ImportSessionReview } from "./features/imports/ImportSessionReview.jsx";
import { ProjectsPage } from "./features/projects/ProjectsPage.jsx";
import { MarketPlanPage } from "./features/plan/MarketPlanPage.jsx";
import { MarketRecordRedirect, marketDrawerRedirect } from "./features/places/MarketRecordRedirect.jsx";
import { AskPopout } from "./features/askPortfolio/AskPopout.jsx";
import { AskContext } from "./contexts/AskContext.jsx";
import { ActionsPanel } from "./components/ActionsPanel.jsx";
import { RadarPage } from "./features/radar/RadarPage.jsx";
import { StartHerePage } from "./v2/starthere/StartHerePage.jsx";
import { ShellV2, TopBarControls, CenteredSearchAnchor } from "./v2/Shell.jsx";
import { UniversalSearchPalette } from "./features/universalSearch/UniversalSearchPalette.jsx";
import { StageBadge } from "./components/StageBadge.jsx";
import { PulsePage } from "./v2/pulse/PulsePage.jsx";
import { commandPillarCountLabel } from "./v2/pulse/commandPillars.js";
import { LeasesPage } from "./v2/leases/LeasesPage.jsx";
import { LeasesSubNav } from "./v2/leases/LeasesSubNav.jsx";
import { isLeasesLandingPath } from "./services/buildUrl.js";
import { LeaseDetailPage } from "./v2/leases/LeaseDetailPage.jsx";
import { RadarDecisionsPanel } from "./v2/radar/RadarDecisionsPanel.jsx";
import { ReviewsRegisterPage } from "./v2/reviews/ReviewsRegisterPage.jsx"; // P-19
import { InFocusRadarPage } from "./v2/pulse/InFocusRadarPage.jsx";
import { RadarSpinePage } from "./v2/radar/RadarSpinePage.jsx";
import { FEATURE_RADAR_SPINE } from "./contexts/FeatureFlagContext.jsx";
import { PlaybooksPage } from "./v2/playbooks/PlaybooksPage.jsx";
import { PlaybookRunPage } from "./v2/playbooks/PlaybookRunPage.jsx";
import { PlaybookRunsPage } from "./v2/playbooks/PlaybookRunsPage.jsx";
import { applyViewGuard, derivePageLabel, deriveNavLocations, deriveNavLeases, resolveNavNeighbors } from "./app/navUtils.js";
import { viewToPath, pathToNavView, returnPathToLabel, buildLocationUrl, buildProjectUrl, buildProjectsUrl, buildImportUrl, buildLeaseUrl, buildMarketPlanUrl, buildMarketUrl } from "./services/buildUrl.js";
import {
  SensitivityPolicyProvider,
  createClientSensitivityPolicy,
} from "./contexts/SensitivityPolicyContext.jsx";
import { recordRecentView } from "./data/searchClient.js";
import { recordPageView, surfaceForPath } from "./data/usageAnalyticsClient.js";
import { FeedbackWidget, SHOW_FEEDBACK_WIDGET } from "./components/FeedbackWidget.jsx";
import { OkrsPage } from "./v2/okrs/OkrsPage.jsx";
import { governedNavEnabled } from "./app/governedNav.js";
import { LEASE_DECISION_WINDOW_SCOPE } from "./shared/leaseDecisionWindow.js";
import { isDataCoverageDocumentPath } from "./shared/dataCoveragePath.js";
import { PROJECT_TAB_SEGMENTS, projectTabFromPath, buildProjectsTabUrl } from "./services/buildUrl.js";
import { PlacesFrame, PlacesAlias, CapacityRedirect } from "./features/places/PlacesFrame.jsx";
import { CapacityPage } from "./features/capacity/CapacityPage.jsx";
import { resolvePlacesLens } from "./app/places.js";
import { marketStateFromOffice } from "./features/places/marketRecord.js";
import { KpiBandProvider } from "./contexts/KpiBandContext.jsx";
import { ProjectsChromeProvider, ProjectsNavigationHost } from "./features/projects/ProjectsChrome.jsx";
import { CollectionChromeProvider, CollectionNavigationHost } from "./components/OneRowChrome.jsx";
import { LocationNavPair } from "./v2/locations/LocationNavPair.jsx";

export function buildProjectsNavigationUrl(projectId, locationId = null, search = "") {
  const params = new URLSearchParams(search);
  if (locationId) params.set("locationId", locationId);
  return projectId
    ? buildProjectUrl(projectId, params)
    : buildProjectsUrl(locationId);
}

export function getDatasetViewPath(datasetKey) {
  return datasetKey === "headcount" ? "/offices" : "/admin";
}

// ─── Internal view-enum constants (mirrors App.jsx) ─────────────────────────
const VIEW_PULSE              = "home";
const VIEW_OFFICES            = "portfolio";
const VIEW_LEASES             = "leases";
const VIEW_LOCATION_DETAIL    = "location-detail";
const VIEW_REPORTS            = "reports-landing";
const VIEW_REPORT_WPR         = "report-wpr";
const VIEW_REPORT_HC_TREND    = "report-headcount-trend";
const VIEW_REPORT_OPEN_ROLES  = "report-open-roles";
const VIEW_REPORT_RTO_COMPLIANCE = "report-rto-compliance";
const VIEW_REPORT_FOOT_TRAFFIC = "report-foot-traffic";
const VIEW_REPORT_SPACE_FIT   = "report-space-fit";
const VIEW_REPORT_COST_LEAKAGE = "report-cost-leakage";
const VIEW_REPORT_UTILIZATION = "report-utilization";
const VIEW_REPORT_DATA_COVERAGE = "report-data-coverage";
const VIEW_DATA_HEALTH        = "data_health";
const VIEW_ADMIN              = "admin";
const VIEW_CURRENCY_RATES     = "currency-rates";
const VIEW_IMPORTS_QUEUE      = "imports-queue";
const VIEW_IMPORTS_SESSION    = "imports-session";
const VIEW_PROJECTS           = "projects";
const VIEW_PROJECT_DETAIL     = "project-detail";
const VIEW_MARKET_PLAN        = "plan";
const VIEW_ROADMAP            = "roadmap";
const VIEW_WORKFLOWS          = "workflows";
const VIEW_OKRS               = "okrs";
const VIEW_RADAR              = "radar";
const VIEW_INFOCUS_RADAR      = "infocus-radar";
const VIEW_PLAYBOOKS          = "playbooks"; // P-38: Playbooks launcher
const VIEW_START_HERE         = "start-here";
const VIEW_RADAR_DECISIONS    = "radar-decisions";
const RADAR_DECISIONS_FLAG    = "FEATURE_RADAR_DECISIONS_REGISTER";
const REPORT_DETAIL_VIEW_KEYS = new Set([
  VIEW_REPORT_WPR,
  VIEW_REPORT_HC_TREND,
  VIEW_REPORT_OPEN_ROLES,
  VIEW_REPORT_SPACE_FIT,
  VIEW_REPORT_COST_LEAKAGE,
  VIEW_REPORT_RTO_COMPLIANCE,
  VIEW_REPORT_FOOT_TRAFFIC,
  VIEW_REPORT_UTILIZATION,
  VIEW_REPORT_DATA_COVERAGE,
]);
const VIEW_REVIEWS            = "reviews";   // P-19: portfolio reviews check-off register
const PORTFOLIO_COMMAND_VIEWS = new Set([
  "datatrust", "plan-coverage", "review-cadence", "portfolio-command",
]);

const REPORT_VIEWS = new Set([
  VIEW_REPORTS, VIEW_REPORT_WPR, VIEW_REPORT_HC_TREND, VIEW_REPORT_OPEN_ROLES,
  VIEW_REPORT_SPACE_FIT,
  VIEW_REPORT_COST_LEAKAGE,
  VIEW_REPORT_RTO_COMPLIANCE,
  VIEW_REPORT_FOOT_TRAFFIC,
  VIEW_REPORT_DATA_COVERAGE,
]);

const PAGE_SUBTITLES = {
  [VIEW_PULSE]:               "Portfolio at a glance, and what needs attention",
  [VIEW_OFFICES]:             "Every office in the portfolio - space, cost, and fit.",
  [VIEW_LEASES]:              "Every lease in the portfolio - cost, terms, and decisions ahead.",
  [VIEW_MARKET_PLAN]:         "Markets portfolio for space planning and scenario analysis.",
  [VIEW_REPORTS]:             "Portfolio reports - headcount, attendance & utilization, space fit, and hiring demand.",
  [VIEW_DATA_HEALTH]:         "Data completeness and field-level quality metrics.",
  [VIEW_ADMIN]:               "Workspace settings, data, and feature configuration.",
  [VIEW_CURRENCY_RATES]:      "Saved conversion basis and currency-rate details.",
  [VIEW_IMPORTS_QUEUE]:       "Review and apply pending location data imports.",
  [VIEW_PROJECTS]:            "Workplace projects across the portfolio - milestones, status, and owners.",
  [VIEW_ROADMAP]:             "Build status of every page and feature - live, partial, or mock.",
  [VIEW_WORKFLOWS]:           "Reusable action templates and in-flight run tracking.",
  [VIEW_OKRS]:                "FY2026 objectives, key results, and initiative progress.",
  [VIEW_RADAR]:               "Portfolio signals, lease events, and headcount trends at a glance.",
  // NAV-RESTRUCTURE-RADAR: updated subtitle for the "Radar" branded page.
  [VIEW_INFOCUS_RADAR]:       "All tracked items across the portfolio, engine-ranked.",
  [VIEW_PLAYBOOKS]:           "Action templates and team playbooks.",
  [VIEW_START_HERE]:          `A four-minute orientation to four key areas of ${APP_NAME}.`,
  [VIEW_RADAR_DECISIONS]:     "Lease renewal intent — every logged decision in one register.",
  [VIEW_REVIEWS]:             "Portfolio review cadence — check off what's been reviewed this cycle.",
};

const PAGE_LABELS_MAP = {
  [VIEW_PULSE]:               "Home",
  [VIEW_OFFICES]:             "Offices",
  [VIEW_LEASES]:              "Leases",
  [VIEW_REPORTS]:             "Reports",
  [VIEW_ADMIN]:               "Admin",
  [VIEW_CURRENCY_RATES]:      "Currency Rates",
  [VIEW_IMPORTS_QUEUE]:       "Pending Imports",
  [VIEW_IMPORTS_SESSION]:     "Import Review",
  [VIEW_PROJECTS]:            "Projects",
  [VIEW_PROJECT_DETAIL]:      "Project",
  [VIEW_MARKET_PLAN]:         "Markets",
  [VIEW_ROADMAP]:             "Roadmap",
  [VIEW_WORKFLOWS]:           "Actions",
  [VIEW_OKRS]:                "OKRs",
  [VIEW_RADAR]:               "Leadership Radar",
  // NAV-RESTRUCTURE-RADAR: InFocusRadarPage is now branded simply as "Radar".
  [VIEW_INFOCUS_RADAR]:       "Radar",
  [VIEW_PLAYBOOKS]:           "Playbooks",
  [VIEW_START_HERE]:          "Start here",
  [VIEW_RADAR_DECISIONS]:     "Radar: Decisions",
  [VIEW_REVIEWS]:             "Reviews",
};

export function isPortfolioCommandView(pathname, search = "") {
  return Boolean(
    pathname?.startsWith("/home") &&
    PORTFOLIO_COMMAND_VIEWS.has(new URLSearchParams(search).get("view")),
  );
}

// Home lease links may explicitly request Timeline, with or without a focus ID.
// Bucket links and bare board links retain their existing destinations.
export function homeLeaseNavigationPath(urlString) {
  const params = new URLSearchParams(String(urlString ?? "").replace(/^\?/, ""));
  if (params.get("view") !== "leases") return null;
  if (params.get("decisionScope") === LEASE_DECISION_WINDOW_SCOPE) {
    const target = new URLSearchParams({ decisionScope: LEASE_DECISION_WINDOW_SCOPE });
    for (const leaseId of params.getAll("decisionLeaseId")) {
      target.append("decisionLeaseId", leaseId);
    }
    return `/leases/timeline?${target.toString()}`;
  }
  const buckets = params.get("buckets");
  if (buckets) return `/leases?buckets=${encodeURIComponent(buckets)}`;
  const focus = params.get("focus");
  if (params.get("leaseView") === "timeline") {
    return focus
      ? `/leases/timeline?focus=${encodeURIComponent(focus)}`
      : "/leases/timeline";
  }
  return "/leases";
}

function PortfolioCommandHeaderStatus({ indexData }) {
  const composite = indexData?.composite ?? null;
  const band = indexData?.band ?? null;
  const bandLabel = { strong: "Strong", watch: "Watch", risk: "At Risk" }[band] ?? band;
  const bandColor = { strong: "var(--v2-green)", watch: "var(--v2-amber)", risk: "var(--v2-red)" }[band] ?? "var(--v2-mid)";

  if (composite === null) return <span>Loading…</span>;
  return (
    <>
      <span>
        Composite <strong style={{ color: bandColor }}>{composite}/100</strong>
      </span>
      <span style={{ color: "var(--v2-line)" }}>·</span>
      {bandLabel && (
        <>
          <span style={{ color: bandColor, fontWeight: T.fwSemibold }}>{bandLabel}</span>
          <span style={{ color: "var(--v2-line)" }}>·</span>
        </>
      )}
       <span>{commandPillarCountLabel(indexData)}</span>
    </>
  );
}

/** Map a URL pathname to the internal view-enum string. */
export function pathnameToView(pathname) {
  if (pathname === "/places" || pathname === "/places/offices") return VIEW_OFFICES;
  if (pathname.startsWith("/places/")) return VIEW_MARKET_PLAN;
  if (!pathname) return VIEW_OFFICES;
  if (pathname === "/" || pathname === "") return VIEW_OFFICES;
  if (pathname.startsWith("/home"))          return VIEW_PULSE;
  if (pathname.startsWith("/offices/"))      return VIEW_LOCATION_DETAIL;
  if (pathname === "/offices")               return VIEW_OFFICES;
  if (pathname.startsWith("/leases"))        return VIEW_LEASES;
  if (pathname.startsWith("/radar/decisions")) return VIEW_RADAR_DECISIONS;
  if (pathname.startsWith("/decisions"))     return VIEW_RADAR_DECISIONS;
  if (pathname.startsWith("/reviews"))       return VIEW_REVIEWS;
  if (pathname.startsWith("/markets"))       return VIEW_MARKET_PLAN;
  if (pathname === "/projects")              return VIEW_PROJECTS;
  if (pathname.startsWith("/projects/")) {
    // Project URLs render the dedicated full project page inside the Projects nav.
    return VIEW_PROJECTS;
  }
  if (pathname === "/reports/wpr")                return VIEW_REPORT_WPR;
  if (pathname === "/reports/headcount-trend")    return VIEW_REPORT_HC_TREND;
  if (pathname === "/reports/open-roles")         return VIEW_REPORT_OPEN_ROLES;
  if (pathname === "/reports/space-fit")          return VIEW_REPORT_SPACE_FIT;
  if (pathname === "/reports/cost-leakage")       return VIEW_REPORT_COST_LEAKAGE;
  if (pathname === "/reports/rto-compliance")     return VIEW_REPORT_RTO_COMPLIANCE;
  if (pathname === "/reports/foot-traffic")       return VIEW_REPORT_FOOT_TRAFFIC;
  if (pathname === "/reports/utilization")        return VIEW_REPORT_UTILIZATION;
  if (isDataCoverageDocumentPath(pathname)) return VIEW_REPORT_DATA_COVERAGE;
  if (pathname === "/reports")               return VIEW_REPORTS;
  if (pathname === "/currency-rates")        return VIEW_CURRENCY_RATES;
  if (pathname.startsWith("/admin/data/import/")) return VIEW_IMPORTS_SESSION;
  if (pathname === "/admin/data/imports")    return VIEW_IMPORTS_QUEUE;
  if (pathname.startsWith("/admin"))         return VIEW_ADMIN;
  if (pathname.startsWith("/workflows"))     return VIEW_WORKFLOWS;
  if (pathname.startsWith("/okrs"))          return VIEW_OKRS;
  if (pathname.startsWith("/roadmap"))       return VIEW_ROADMAP;
  if (pathname.startsWith("/infocus-radar")) return VIEW_INFOCUS_RADAR;
  if (pathname.startsWith("/radar"))         return VIEW_RADAR;
  if (pathname.startsWith("/playbooks"))     return VIEW_PLAYBOOKS;
  if (pathname.startsWith("/start-here"))    return VIEW_START_HERE;
  return VIEW_OFFICES;
}
// Governed destinations must never fall back to Offices when Offices itself is
// disabled. The first enabled destination is a safe landing target for direct
// links and for a user who is already on a page when an admin disables it.
const GOVERNED_NAV_PATHS = {
  home: "/home",
  places: "/places",
  portfolio: "/offices",
  leases: "/leases",
  plan: "/markets",
  projects: "/projects",
  "reports-landing": "/reports",
  reports: "/reports",
  radar: "/infocus-radar",
  playbooks: "/playbooks",
  roadmap: "/roadmap",
  workflows: "/workflows",
  okrs: "/okrs",
  reviews: "/reviews",
};

export function isGovernedNavEnabled(flags, navConfig, navKey) {
  if (Array.isArray(navConfig) && navConfig.length === 0) return false;
  return governedNavEnabled(flags, navConfig, navKey === "reports-landing" ? "reports" : navKey);
}

export function safeEnabledNavPath(flags, navConfig, currentRole, canSee = () => false) {
  const configuredKeys = Array.isArray(navConfig)
    ? navConfig.map((entry) => entry.key)
    : Object.keys(GOVERNED_NAV_PATHS);
  for (const navKey of configuredKeys) {
    if (navKey === "places" && !resolvePlacesLens(null, flags?.places_tabs)) continue;
    const path = GOVERNED_NAV_PATHS[navKey];
    if (!path || (navKey === "admin" && currentRole !== "admin")) continue;
    if (navKey === "leases" && !canSee("leases.page")) continue;
    if (isGovernedNavEnabled(flags, navConfig, navKey)) return path;
  }
  // No enabled governed destination remains. Do not redirect to /admin:
  // non-admin route guards redirect /admin back to the disabled route.
  return "/nav-unavailable";
}

export function GovernedDestination({ enabled, flags, navConfig, currentRole, canSee, children }) {
  const { flagsReady } = useFeatureFlagContext();
  // Do not redirect a cold record bookmark using provisional lens defaults.
  // The public collection may still be in flight after authentication settles.
  if (flagsReady === false) return null;
  if (enabled === false) {
    return <Navigate to={safeEnabledNavPath(flags, navConfig, currentRole, canSee)} replace />;
  }
  return children;
}

function GovernedNavUnavailable() {
  return (
    <section
      data-testid="governed-nav-unavailable"
      style={{ padding: 32, maxWidth: 640, margin: "0 auto" }}
    >
      <h1>No destinations are enabled</h1>
      <p>An administrator must enable at least one workspace destination before continuing.</p>
    </section>
  );
}

export function governedKeyForView(view) {
  return {
    [VIEW_PULSE]: "home", [VIEW_OFFICES]: "places",
    [VIEW_LOCATION_DETAIL]: "places", [VIEW_LEASES]: "leases",
    [VIEW_PROJECTS]: "projects", [VIEW_PROJECT_DETAIL]: "projects",
    [VIEW_MARKET_PLAN]: "places", [VIEW_ROADMAP]: "roadmap",
    [VIEW_WORKFLOWS]: "workflows", [VIEW_OKRS]: "okrs",
    [VIEW_RADAR]: "radar", [VIEW_INFOCUS_RADAR]: "radar",
    [VIEW_RADAR_DECISIONS]: "radar",
    [VIEW_PLAYBOOKS]: "playbooks", [VIEW_REVIEWS]: "reviews",
    [VIEW_REPORTS]: "reports",
  }[view] ?? (REPORT_DETAIL_VIEW_KEYS.has(view) ? "reports" : null);
}

// A bare legacy list URL always means the Markets list, not a remembered lens.
export function MarketPlanLandingRedirect() {
  return <PlacesAlias lens="markets" />;
}

// Old Market Plan bookmarks redirect to the new Markets namespace. The bare
// legacy path intentionally lands on /markets so its persisted-tab behavior
// remains centralized in MarketPlanLandingRedirect.
export function LegacyMarketPlanRedirect() {
  const { pathname, search, hash, state } = useLocation();
  const detailMatch = useMatch("/market-plan/markets/:marketId");
  const tabMatch = useMatch("/market-plan/markets/:marketId/:recordTab");
  const legacyTab = pathname.slice("/market-plan".length);
  const tabByPath = {
    "": "plan",
    "/plan": "plan",
    "/strategy": "strategy",
    "/pressure": "market_pressure",
    "/scenario": "whatif",
    "/guidance": "recommendations",
  };
  // PLACES-CAPACITY-UNIFIED: retired lenses normalize through CapacityRedirect
  // (buildMarketPlanUrl already carries a query, so appending search would double "?").
  if (!detailMatch && (legacyTab === "/pressure" || legacyTab === "/scenario")) {
    return <CapacityRedirect fromLens={legacyTab.slice(1)} />;
  }
  const record = tabMatch ?? detailMatch;
  if (record) return <Navigate to={marketDrawerRedirect({ ...record.params, search, hash, state })} state={state} replace />;
  const target = buildMarketPlanUrl(tabByPath[legacyTab] ?? "plan");
  return <Navigate to={target + search + hash} state={state} replace />;
}

// ─── PulseRedirect ───────────────────────────────────────────────────────────
// Redirects /pulse (and /pulse?view=…) → /home (preserving query params).
function PulseRedirect() {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  return <Navigate to={search ? `/home?${search}` : "/home"} replace />;
}

// ─── PulsePageWithView ───────────────────────────────────────────────────────
// Reads ?view= once per render and passes it as `pulseView` to PulsePage.
// Defined here (inside a <Route>) so useSearchParams is always within a
// Router context and avoids the useSearchParams-in-Route nav-trap.
function PulsePageWithView(props) {
  const [searchParams] = useSearchParams();
  const pulseView = searchParams.get("view") ?? null;
  return <PulsePage {...props} pulseView={pulseView} />;
}

// ─── DefaultLandingRoute ────────────────────────────────────────────────────
// Handles "/" and all legacy ?view= / ?actionNav= deep-link params.
// Reads currentUser + flags via props passed from AppShell (no context needed).
function DefaultLandingRoute({ currentUser, flags, navConfig, navContext, onRestoreNavState }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const actionNav     = searchParams.get("actionNav");
    const actionNavId   = searchParams.get("actionNavId");
    const actionNavLocId = searchParams.get("actionNavLocId");
    const urlView       = searchParams.get("view");
    const urlProjectId  = searchParams.get("projectId");
    const rawView       = searchParams.get("view");

    if (rawView === "report-headcount-trend" && flags["report-headcount-trend"]) {
      navigate("/reports/headcount-trend", { replace: true });
    } else if (urlView === "radar") {
      navigate("/radar", { replace: true });
    } else if (urlView === "projects" && isGovernedNavEnabled(flags, navConfig, "projects")) {
      if (urlProjectId) {
        navigate(buildProjectsNavigationUrl(urlProjectId, searchParams.get("locationId"), searchParams), { replace: true });
      } else {
        navigate(buildProjectsNavigationUrl(null, searchParams.get("locationId")), { replace: true });
      }
    } else if (actionNav && actionNavId && flags.FEATURE_ACTIONS_BELL) {
      if (actionNav === "location") {
        navigate(`/offices/${actionNavId}`, {
          replace: true,
          state: { returnPath: "/offices" },
        });
      } else if (actionNav === "lease" && actionNavLocId) {
        navigate(`/offices/${actionNavLocId}`, {
          replace: true,
          state: { returnPath: "/offices", initTab: "lease", leaseId: actionNavId },
        });
      } else {
        navigate("/offices", { replace: true });
      }
    } else {
      // Normal default landing — use the server-computed defaultView when
      // available; fall back to /offices.
      const target =
        currentUser?.defaultView === "home" &&
          isGovernedNavEnabled(flags, navConfig, "home")
          ? "/home"
          : isGovernedNavEnabled(flags, navConfig, "portfolio")
            ? "/offices"
            : safeEnabledNavPath(flags, navConfig);
      navigate(target, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

// ─── LegacyImportRedirect ───────────────────────────────────────────────────
function LegacyImportRedirect() {
  const match = useMatch("/imports/:sessionId");
  const sessionId = match?.params?.sessionId;
  return <Navigate to={sessionId ? `/admin/data/import/${sessionId}` : "/admin/data/imports"} replace />;
}

// ─── AppShell ───────────────────────────────────────────────────────────────
export function AppShell() {
  useInteractionLayer();

  const navigate      = useNavigate();
  const routerLoc     = useLocation();
  const { pathname }  = routerLoc;

  // URL-derived IDs (via useMatch — available outside Routes because AppShell
  // wraps the Routes tree).
  const locationMatch     = useMatch("/offices/:locationId");
  const locationTabMatch  = useMatch("/offices/:locationId/:tab");
  const projectMatch      = useMatch("/projects/:projectId");
  const marketMatch       = useMatch("/markets/:marketId");
  const leaseMatch        = useMatch("/leases/:leaseId");
  const leaseTabMatch     = useMatch("/leases/:leaseId/:tab");
  const importSessionMatch = useMatch("/admin/data/import/:sessionId");

  const locationIdFromPath    = locationMatch?.params?.locationId    ?? locationTabMatch?.params?.locationId    ?? null;
  const tabFromPath           = locationTabMatch?.params?.tab        ?? null;
  const rawProjectId          = projectMatch?.params?.projectId      ?? null;
  const projectIdFromPath     = projectTabFromPath(pathname) ? null : rawProjectId;
  const isMarketTabPath        = /^\/markets\/(?:plan|strategy|pressure|scenario|guidance)\/?$/.test(pathname);
  const marketIdFromPath      = pathname === "/places/markets"
    ? new URLSearchParams(routerLoc.search).get("peek")
    : !isMarketTabPath
    ? (marketMatch?.params?.marketId ?? null)
    : null;
  const leaseIdFromPath       = isLeasesLandingPath(routerLoc.pathname) ? null : leaseMatch?.params?.leaseId ?? leaseTabMatch?.params?.leaseId ?? null;
  const importSessionIdFromPath = importSessionMatch?.params?.sessionId ?? null;

  // Current view derived from URL (used for shell decorations, back-label etc.)
  const view = pathnameToView(pathname);
  const projectsLocationId = pathname.startsWith("/projects")
    ? new URLSearchParams(routerLoc.search).get("locationId")
    : null;

  // Back navigation: return path + tab are stored in router location.state
  // when navigating TO a location/project/etc.
  const detailReturnPath = routerLoc.state?.returnPath ?? "/offices";
  const detailInitTab    = tabFromPath ?? routerLoc.state?.initTab ?? "building";
  const backLabel        = returnPathToLabel(detailReturnPath);

  // ─── Auth ──────────────────────────────────────────────────────────────
  const flags = useFeatureFlags();
  const { reportFlags, reportAccess, nav_config, nav_order, flagsReady, placesTabs } = useFeatureFlagContext();
  const commandView = new URLSearchParams(routerLoc.search).get("view");
  const isPortfolioCommand = isPortfolioCommandView(pathname, routerLoc.search)
    && (commandView !== "plan-coverage" || isGovernedNavEnabled(flags, nav_config, "plan"));

  const [currentUser,  setCurrentUser]  = useState(null);
  const [actualRole,   setActualRole]   = useState(null);
  const [previewRole,  setPreviewRole]  = useState(null);
  const currentRole = previewRole ?? actualRole;
  const weeklyReadIdentity = JSON.stringify([currentUser?.id, currentUser?.devActAs, currentRole]);
  useLayoutEffect(() => { setWeeklyReadIdentity(weeklyReadIdentity); }, [weeklyReadIdentity]);
  const [probeFailed,  setProbeFailed]  = useState(false);
  const [authChecked,  setAuthChecked]  = useState(false);
  const dataCoverageAllowed = authChecked && flagsReady
    && reportAccess?.["report-data-coverage"] === true
    && (currentRole === "admin" || currentRole === "editor");
  const [portfolioCommandIndex, setPortfolioCommandIndex] = useState(null);
  const cancelledRef   = useRef(false);
  const hasNavigated   = useRef(false);
  const locationDetailRef = useRef(null);
  const sensitivity = useMemo(
    () => createClientSensitivityPolicy({
      role: currentRole,
      taxonomy: currentUser?.sensitivityPolicy,
      ready: authChecked,
    }),
    [authChecked, currentRole, currentUser?.sensitivityPolicy],
  );
  const universalSearchCategoryVisibility = {
    offices: governedNavEnabled(flags, nav_config, "places", nav_order)
      && resolvePlacesLens("offices", placesTabs) === "offices"
      && sensitivity.canSee("home.page"),
    leases: governedNavEnabled(flags, nav_config, "leases", nav_order)
      && sensitivity.canSee("leases.page"),
    markets: governedNavEnabled(flags, nav_config, "places", nav_order)
      && resolvePlacesLens("markets", placesTabs) === "markets"
      && sensitivity.canSee("home.page"),
    projects: governedNavEnabled(flags, nav_config, "projects", nav_order)
      && sensitivity.canSee("home.page"),
    reports: governedNavEnabled(flags, nav_config, "reports", nav_order)
      && sensitivity.canSee("home.page"),
  };
  const universalSearchReportKeys = reportFlags
    .filter((report) => report.enabled !== false)
    .filter((report) => report.key !== VIEW_REPORT_DATA_COVERAGE || dataCoverageAllowed)
    .map((report) => report.key);

  const runProbe = useCallback((isRetry) => {
    return Promise.all([getCurrentUser(), getDevActAsStatus().catch(() => ({ enabled: false }))])
      .then(([user, devStatus]) => {
        if (cancelledRef.current) return;
        const serverRole = user?.role === "view_limited" ? "viewer" : user?.role ?? "unknown";
        const savedPreview = getClientRolePreview();
        setCurrentUser(user);
        setActualRole(serverRole);
        if (devStatus.enabled && !devStatus.person && !devStatus.invalid && !user?.devActAs && serverRole === "admin" && savedPreview && savedPreview !== serverRole) {
          setPreviewRole(savedPreview);
        } else {
          clearClientRolePreview();
          setPreviewRole(null);
        }
        setProbeFailed(false);
        setAuthChecked(true);
      })
      .catch((err) => {
        if (cancelledRef.current) return;
        if (err instanceof AuthError && err.status === 401) {
          log.ui("AppShell: unauthenticated - redirecting to login");
          const returnTo = encodeURIComponent(window.location.pathname || "/");
          window.location.href = `/login?returnTo=${returnTo}`;
          return;
        }
        log.error("AppShell: /api/me probe failed", { err: String(err), isRetry });
        if (!isRetry) {
          setTimeout(() => { if (!cancelledRef.current) runProbe(true); }, 2000);
        } else {
          setCurrentUser(null);
          setActualRole("unknown");
          setPreviewRole(null);
          setProbeFailed(true);
          setAuthChecked(true);
        }
      });
  }, []);

  // Profile photo writes patch identity in place (role/devActAs preserved).
  useEffect(() => subscribeCurrentUser((patch) => {
    setCurrentUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }), []);

  useEffect(() => {
    log.ui("AppShell mounted", { devMode: import.meta.env.DEV });
    cancelledRef.current = false;
    runProbe(false);
    return () => { cancelledRef.current = true; };
  }, [runProbe]);

  useEffect(() => {
    if (!authChecked || !isPortfolioCommand) {
      if (!isPortfolioCommand) setPortfolioCommandIndex(null);
      return;
    }
    let cancelled = false;
    setPortfolioCommandIndex(null);
    getCommandIndex()
      .then((result) => { if (!cancelled) setPortfolioCommandIndex(result ?? null); })
      .catch(() => { if (!cancelled) setPortfolioCommandIndex(null); });
    return () => { cancelled = true; };
  }, [authChecked, isPortfolioCommand]);

  const onManualRetry = () => { setProbeFailed(false); runProbe(false); };
  const handleDemoRoleChange = useCallback(async (role) => {
    if (actualRole !== "admin" || currentUser?.devActAs || !["admin", "editor", "viewer"].includes(role)) return;
    let state;
    try { state = await getDevActAsStatus(); } catch { return; }
    if (!state.enabled || state.person || state.invalid) return;
    if (role === actualRole) {
      clearClientRolePreview();
      setPreviewRole(null);
      return;
    }
    if (setClientRolePreview(role)) setPreviewRole(role);
  }, [actualRole, currentUser]);
  const handleExitRolePreview = useCallback(() => {
    clearClientRolePreview();
    setPreviewRole(null);
  }, []);

  // ─── Shared state ────────────────────────────────────────────────────
  const [addLocationModalOpen, setAddLocationModalOpen] = useState(false);
  const [portfolioRefreshKey,  setPortfolioRefreshKey]  = useState(0);
  const [askOpen,              setAskOpen]              = useState(false);
  const [askSearchDraft, setAskSearchDraft] = useState(null);
  const [askScope,             setAskScope]             = useState(null);
  const [askOpenedViaShortcut, setAskOpenedViaShortcut] = useState(false);
  const [universalSearchOpen,  setUniversalSearchOpen]  = useState(false);
  const [universalSearchQuery, setUniversalSearchQuery] = useState("");
  const universalSearchInputRef = useRef(null);
  const [actionsPanelOpen,     setActionsPanelOpen]     = useState(false);
  const [actionsCountKey,      setActionsCountKey]       = useState(0);
  const [navContext,           setNavContext]            = useState(() => loadLastListView() ?? "locations");
  const [selectedLeaseId,      setSelectedLeaseId]      = useState(() => loadCurrentNavLease() ?? null);
  const [allLeases,            setAllLeases]            = useState([]);
  const [leasesSubtitle,       setLeasesSubtitle]       = useState(null);
  const [planData,             setPlanData]             = useState([]);
  const [cockpitActiveView,    setCockpitActiveView]    = useState(null);
  const [portfolioLocations,   setPortfolioLocations]   = useState([]);
  const [search,               setSearch]               = useState("");
  const [planDeepLinkLocationId, setPlanDeepLinkLocationId] = useState(null);
  const [planDeepLinkMarketId,   setPlanDeepLinkMarketId]   = useState(null);
  const [decisionFocusLocationId, setDecisionFocusLocationId] = useState(null);
  const [workflowLaunchSource,   setWorkflowLaunchSource]   = useState(null);
  const [openWorkflowRunId,      setOpenWorkflowRunId]       = useState(null);
  const [importsQueueReturnSessionId, setImportsQueueReturnSessionId] = useState(null);

  // NAV-PREVNEXT-RESPECT-FILTER-SCOPE: Page-supplied ordered ID lists.
  // Null = page not yet mounted (deep-link entry) → fall back to persisted state.
  // Set = page is mounted and has reported its current filtered+sorted sequence.
  const [officesNavIds,  setOfficesNavIds]  = useState(null);
  const [leasesNavIds,   setLeasesNavIds]   = useState(null);
  // REPORTS-PREVNEXT-NAV: Ordered report key list pushed by ReportsPage.
  // Null means the landing page has not reported its list yet (for example,
  // direct report entry), so the shell uses the same feature-flag order as
  // the landing page until that page publishes its current sequence.
  const [reportNavList,  setReportNavList]  = useState(null);
  const lastRecordedRecentView = useRef(null);

  useEffect(() => {
    const entities = [];
    if (locationIdFromPath) {
      entities.push({ type: "office", id: locationIdFromPath });
      const leaseId = routerLoc.state?.initTab === "lease"
        ? (routerLoc.state?.leaseId ?? selectedLeaseId)
        : null;
      if (leaseId) entities.push({ type: "lease", id: leaseId });
    }
    if (marketIdFromPath) entities.push({ type: "market", id: marketIdFromPath });
    if (projectIdFromPath) entities.push({ type: "project", id: projectIdFromPath });
    if (leaseIdFromPath) entities.push({ type: "lease", id: leaseIdFromPath });
    if ([
      VIEW_REPORT_WPR,
      VIEW_REPORT_HC_TREND,
      VIEW_REPORT_OPEN_ROLES,
      VIEW_REPORT_SPACE_FIT,
      VIEW_REPORT_COST_LEAKAGE,
      VIEW_REPORT_RTO_COMPLIANCE,
      VIEW_REPORT_FOOT_TRAFFIC,
      VIEW_REPORT_UTILIZATION,
    ].includes(view)) {
      entities.push({ type: "report", id: view });
    }
    if (entities.length === 0) return;

    const viewKey = `${routerLoc.key}:${entities.map((entity) => `${entity.type}:${entity.id}`).join("|")}`;
    if (lastRecordedRecentView.current === viewKey) return;
    lastRecordedRecentView.current = viewKey;
    for (const entity of entities) {
      recordRecentView(entity.type, entity.id)
        .catch((err) => log.debug("AppShell: record recent entity view failed", { err: String(err) }));
    }
  }, [
    locationIdFromPath,
    leaseIdFromPath,
    marketIdFromPath,
    projectIdFromPath,
    view,
    routerLoc.key,
    routerLoc.state?.initTab,
    routerLoc.state?.leaseId,
    selectedLeaseId,
  ]);

  // USAGE-ANALYTICS: capture one canonical page view per top-level route
  // transition, not one event per API request. This is intentionally
  // fire-and-forget so navigation never waits on telemetry.
  const lastRecordedPageView = useRef(null);
  useEffect(() => {
    if (!authChecked || !currentUser?.id) return;
    const surface = surfaceForPath(pathname);
    const viewKey = `${routerLoc.key}:${surface}`;
    if (lastRecordedPageView.current === viewKey) return;
    lastRecordedPageView.current = viewKey;
    void recordPageView(surface).catch((err) => {
      log.debug("AppShell: record usage page view failed", { err: String(err) });
    });
  }, [authChecked, currentUser?.id, pathname, routerLoc.key]);

  // ─── Core navigation handler ─────────────────────────────────────────
  const goToView = useCallback((targetView) => {
    hasNavigated.current = true;
    navigate(viewToPath(targetView));
  }, [navigate]);


  // ─── Location detail navigation ─────────────────────────────────────
  const openLocationFromPortfolio = useCallback((locId, tab, orderedIds) => {
    hasNavigated.current = true;
    saveLastListView("locations");
    setNavContext("locations");
    navigate(buildLocationUrl(locId, tab ?? null), {
      state: {
        returnPath: `${routerLoc.pathname}${routerLoc.search}${routerLoc.hash}`,
        initTab: tab ?? "building",
        ...(Array.isArray(orderedIds) && orderedIds.length > 0 ? { orderedIds } : {}),
      },
    });
  }, [navigate, routerLoc.pathname, routerLoc.search, routerLoc.hash]);

  const openLocationFromReport = useCallback((reportView) => (locId, tab) => {
    hasNavigated.current = true;
    saveLastListView("locations");
    setNavContext("locations");
    navigate(buildLocationUrl(locId, tab ?? null), {
      state: { returnPath: viewToPath(reportView), initTab: tab ?? "building" },
    });
  }, [navigate]);

  const openLocationFromLeasesNav = useCallback((locId, tab, leaseId) => {
    hasNavigated.current = true;
    saveLastListView("leases");
    setNavContext("leases");
    if (leaseId) {
      setSelectedLeaseId(leaseId);
      saveCurrentNavLease(leaseId);
      saveCurrentNavLeaseLocation(locId);
    }
    navigate(buildLocationUrl(locId, tab ?? "lease"), {
      state: { returnPath: "/leases", initTab: tab ?? "lease", leaseId: leaseId ?? null },
    });
  }, [navigate]);

  const backFromDetail = useCallback(() => {
    hasNavigated.current = true;
    navigate(detailReturnPath, {
      state: /^\/markets\/[^/]+(?:\/|$)/.test(detailReturnPath)
        ? marketStateFromOffice(routerLoc.state) : null,
    });
  }, [navigate, detailReturnPath, routerLoc.state]);

  const handleOfficeArchived = useCallback((id) => {
    // The mutation already invalidated data caches. Remove the parent from
    // the shell's long-lived pager inventory before leaving its detail.
    setPortfolioLocations((rows) => rows.filter((row) => row.id !== id));
  }, []);

  // ─── Project navigation ──────────────────────────────────────────────
  const openProjectDetail = useCallback((id) => {
    hasNavigated.current = true;
    navigate(buildProjectUrl(id));
  }, [navigate]);

  const backFromProjectDetail = useCallback(() => {
    hasNavigated.current = true;
    navigate("/projects");
  }, [navigate]);

  // ─── Import navigation ───────────────────────────────────────────────
  const openImportsQueue = useCallback((originSessionId) => {
    hasNavigated.current = true;
    if (originSessionId) setImportsQueueReturnSessionId(originSessionId);
    navigate("/admin/data/imports");
  }, [navigate]);

  const openImportsSession = useCallback((sessionId) => {
    hasNavigated.current = true;
    navigate(buildImportUrl(sessionId));
  }, [navigate]);

  const backFromImports = useCallback(() => {
    hasNavigated.current = true;
    if (importsQueueReturnSessionId !== null) {
      const returnId = importsQueueReturnSessionId;
      setImportsQueueReturnSessionId(null);
      navigate(buildImportUrl(returnId));
      return;
    }
    try { localStorage.setItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY, "data"); } catch (_) {}
    navigate("/admin");
  }, [navigate, importsQueueReturnSessionId]);

  const openViewDataset = useCallback((datasetKey) => {
    hasNavigated.current = true;
    const destination = getDatasetViewPath(datasetKey);
    if (destination === "/admin") {
      try { localStorage.setItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY, datasetKey ?? "data"); } catch (_) {}
    }
    navigate(destination);
  }, [navigate]);

  // ─── Workflow navigation ─────────────────────────────────────────────
  const startWorkflowFrom = useCallback((source) => {
    if (!isGovernedNavEnabled(flags, nav_config, "workflows")) return;
    hasNavigated.current = true;
    setWorkflowLaunchSource(source);
    navigate("/workflows");
  }, [navigate, flags, nav_config]);

  const openLinkedWorkflowRun = useCallback((runId) => {
    if (!isGovernedNavEnabled(flags, nav_config, "workflows")) return;
    hasNavigated.current = true;
    setOpenWorkflowRunId(runId);
    navigate("/workflows");
  }, [navigate, flags, nav_config]);

  const navigateFromWorkflowRun = useCallback((source) => {
    if (!source) return;
    hasNavigated.current = true;
    if (source.type === "location" && source.locationId) {
      setNavContext("locations");
      navigate(buildLocationUrl(source.locationId), {
        state: { returnPath: "/workflows" },
      });
    } else if (source.type === "market" && source.marketId) {
      setPlanDeepLinkMarketId(source.marketId);
      navigate(buildMarketPlanUrl("plan"));
    } else if (source.type === "decision" && source.locationId) {
      setDecisionFocusLocationId(source.locationId);
      navigate("/home");
    }
  }, [navigate]);

  // ─── Plan data ────────────────────────────────────────────────────────
  const refreshPlanData = useCallback(() => {
    return getPlan()
      .then((data) => {
        const markets = Array.isArray(data) ? data : (Array.isArray(data?.markets) ? data.markets : []);
        setPlanData(markets);
        return markets;
      })
      .catch((err) => {
        log.error("AppShell: refreshPlanData failed", { err: String(err) });
      });
  }, []);

  useEffect(() => {
    // Plan markets are also the canonical source for the Offices KPI. The
    // endpoint applies its own sensitivity shaping, so this preload must not
    // depend on the leases-page capability (Workplace View can access Offices
    // while leases.page is denied).
    if (!authChecked) return undefined;
    let cancelled = false;
    getPlan()
      .then((data) => {
        if (!cancelled) {
          const markets = Array.isArray(data) ? data : (Array.isArray(data?.markets) ? data.markets : []);
          setPlanData(markets);
        }
      })
      .catch((err) => { log.error("AppShell: getPlan pre-fetch failed", { err: String(err) }); });
    return () => { cancelled = true; };
  }, [authChecked]);

  // ─── Plan invalidation bus subscription ──────────────────────────────
  // AppShell must stay subscribed to plan invalidation at all times — not
  // just when MarketPlanPage is mounted.  When the user edits a location
  // from the Overview tab (stage, capacity, alias…), locationsClient calls
  // invalidatePortfolioCaches() which notifies every registered subscriber.
  // MarketPlanPage tears down its own subscription on unmount, so without
  // this effect AppShell's planData state would remain stale until the next
  // full reload.  Once refreshed here, MarketPlanPage initialises its local
  // planRows from the up-to-date planDataProp on its next mount.
  useEffect(() => {
    subscribePlanInvalidation(refreshPlanData);
    return () => { unsubscribePlanInvalidation(refreshPlanData); };
  }, [refreshPlanData]);

  // ─── Portfolio locations ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    getLocations()
      .then((list) => { if (!cancelled) setPortfolioLocations(Array.isArray(list) ? list : []); })
      .catch((err) => { log.error("AppShell: getLocations failed", { err: String(err) }); });
    return () => { cancelled = true; };
  }, []);

  // ─── Portfolio leases ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    getPortfolioLeases()
      .then((list) => { if (!cancelled) setAllLeases(Array.isArray(list) ? list : []); })
      .catch((err) => { log.error("AppShell: getPortfolioLeases failed", { err: String(err) }); });
    return () => { cancelled = true; };
  }, []);

  // ─── Ask context ──────────────────────────────────────────────────────
  const currentAskScope = useMemo(() => {
    if (!flags.FEATURE_ASK_PORTFOLIO_NAV) return null;
    if (view === VIEW_LOCATION_DETAIL && locationIdFromPath) {
      const loc = (portfolioLocations || []).find((l) => l.id === locationIdFromPath);
      return { type: "location", id: locationIdFromPath, label: loc?.market || locationIdFromPath };
    }
    return null;
  }, [flags.FEATURE_ASK_PORTFOLIO_NAV, view, locationIdFromPath, portfolioLocations]);

  const openAskFromContext = useCallback((overrideScope) => {
    setAskSearchDraft(null);
    setAskScope(overrideScope !== undefined ? overrideScope : currentAskScope);
    setAskOpenedViaShortcut(false);
    setAskOpen(true);
  }, [currentAskScope]);

  const openAskFromSearch = useCallback((query) => {
    if (!flags.FEATURE_ASK_PORTFOLIO_NAV) return;
    setUniversalSearchOpen(false);
    setAskScope(null);
    setAskOpenedViaShortcut(false);
    setAskSearchDraft((previous) => ({ query, sequence: (previous?.sequence ?? 0) + 1 }));
    setAskOpen(true);
  }, [flags.FEATURE_ASK_PORTFOLIO_NAV]);

  const askContextValue = useMemo(() => ({
    onAskOpen: openAskFromContext,
    currentAskScope,
  }), [openAskFromContext, currentAskScope]);

  // ─── Universal search shortcut ────────────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e) {
      const tag = (document.activeElement?.tagName ?? "").toUpperCase();
      const universalSearchInput = document.activeElement?.matches?.(
        '[data-testid="universal-search-input"]',
      );
      const insideUniversalSearch = document.activeElement?.closest?.(
        '[data-testid="universal-search-palette"]',
      );
      const editable = !universalSearchInput && !insideUniversalSearch && (
        document.activeElement?.isContentEditable
        || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
      );
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && (!editable || universalSearchInput)) {
        e.preventDefault();
        setUniversalSearchOpen(true);
        universalSearchInputRef.current?.focus();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const openUniversalSearch = useCallback(() => {
    setUniversalSearchOpen(true);
    universalSearchInputRef.current?.focus();
  }, []);
  const closeUniversalSearch = useCallback(() => {
    setUniversalSearchOpen(false);
    setUniversalSearchQuery("");
  }, []);
  const clearUniversalSearch = useCallback(() => {
    setUniversalSearchQuery("");
    universalSearchInputRef.current?.focus();
  }, []);
  const handleUniversalSearchNavigate = useCallback((url) => {
    navigate(url);
  }, [navigate]);
  const handleUniversalSearchAction = useCallback((actionId) => {
    if (actionId === "add-office") {
      setAddLocationModalOpen(true);
    } else if (actionId === "go-to-reports") {
      navigate("/reports");
    }
  }, [navigate]);

  // ─── Prev/Next location navigation ───────────────────────────────────
  const NOW_FOR_LEASE_NAV = useMemo(() => new Date(), []);

  // NAV-PREVNEXT-RESPECT-FILTER-SCOPE: When OfficesPage is mounted it pushes its
  // filtered+sorted ID list via setOfficesNavIds.  Use that directly so Prev/Next
  // respects the active filter.  Fall back to the persisted-state path only when
  // the page hasn't reported a list yet (e.g. the user arrived via a deep link).
  const { prevLocation, nextLocation } = useMemo(() => {
    if (officesNavIds !== null) {
      const { prev, next } = resolveNavNeighbors(locationIdFromPath, officesNavIds, portfolioLocations);
      return { prevLocation: prev, nextLocation: next };
    }
    const persisted = loadPortfolioState() ?? DEFAULT_PORTFOLIO_STATE;
    const ranked = rankSites(portfolioLocations);
    return deriveNavLocations(locationIdFromPath, ranked, persisted, search);
  }, [locationIdFromPath, portfolioLocations, search, officesNavIds]);

  // NAV-PREVNEXT-RESPECT-FILTER-SCOPE: Same pattern for the lease-context pager
  // on the location detail page.  LeasesPage pushes its filtered lease ID list
  // via setLeasesNavIds; use that when available, fall back otherwise.
  const { prevLease, nextLease } = useMemo(() => {
    if (navContext !== "leases" || !selectedLeaseId) {
      return { prevLease: null, nextLease: null };
    }
    if (leasesNavIds !== null) {
      const pick = (l) => ({ id: l.id, locationId: l.locationId, city: l.city });
      const { prev, next } = resolveNavNeighbors(selectedLeaseId, leasesNavIds, allLeases, pick);
      return { prevLease: prev, nextLease: next };
    }
    const persistedFilters = loadLeasesFilters();
    const persistedSort    = loadLeasesSort();
    const result = deriveNavLeases(selectedLeaseId, allLeases, NOW_FOR_LEASE_NAV, persistedFilters, persistedSort);
    if (result.prevLease === null && result.nextLease === null && allLeases.length > 0) {
      const inList = allLeases.some((l) => l.id === selectedLeaseId);
      if (!inList) {
        log.warn("AppShell: lease not found in portfolio lease list - lease nav arrows suppressed", { leaseId: selectedLeaseId });
      }
    }
    return result;
  }, [navContext, selectedLeaseId, allLeases, NOW_FOR_LEASE_NAV, leasesNavIds]);

  // ─── Report Prev/Next navigation ─────────────────────────────────────
  // REPORTS-PREVNEXT-NAV: Derive the adjacent report keys from the nav list
  // that ReportsPage pushes via onNavListChange. When it is not mounted, use
  // the same enabled/admin-configured sequence that drives its landing tiles.
  // `view` equals the report key constant when on a report detail page.
  const fallbackReportNavList = useMemo(
    () => getOrderedReportKeys(reportFlags, flagsReady, dataCoverageAllowed),
    [flagsReady, reportFlags, dataCoverageAllowed],
  );

  const { prevReport, nextReport } = useMemo(() => {
    const navList = (reportNavList ?? fallbackReportNavList)
      .filter((key) => fallbackReportNavList.includes(key));
    if (!REPORT_DETAIL_VIEW_KEYS.has(view) || navList.length === 0) {
      return { prevReport: null, nextReport: null };
    }
    const idx = navList.indexOf(view);
    if (idx < 0) return { prevReport: null, nextReport: null };
    const prevKey = idx > 0 ? navList[idx - 1] : null;
    const nextKey = idx < navList.length - 1 ? navList[idx + 1] : null;
    return {
      prevReport: prevKey ? { key: prevKey, title: REPORT_TILE_TITLES[prevKey] ?? prevKey } : null,
      nextReport: nextKey ? { key: nextKey, title: REPORT_TILE_TITLES[nextKey] ?? nextKey } : null,
    };
  }, [view, reportNavList, fallbackReportNavList]);

  const onNavigateReport = useCallback((key) => {
    hasNavigated.current = true;
    navigate(viewToPath(key));
  }, [navigate]);

  const goToNextLocation = useCallback(async () => {
    if (!nextLocation) return;
    const proceed = await locationDetailRef.current?.onBeforeNavigate?.("next", nextLocation.market);
    if (proceed === false) return;
    navigate(buildLocationUrl(nextLocation.id, tabFromPath ?? null), {
      state: { ...routerLoc.state, returnPath: detailReturnPath, initTab: tabFromPath ?? "building" },
    });
  }, [nextLocation, navigate, detailReturnPath, tabFromPath, routerLoc.state]);

  const goToPrevLocation = useCallback(async () => {
    if (!prevLocation) return;
    const proceed = await locationDetailRef.current?.onBeforeNavigate?.("prev", prevLocation.market);
    if (proceed === false) return;
    navigate(buildLocationUrl(prevLocation.id, tabFromPath ?? null), {
      state: { ...routerLoc.state, returnPath: detailReturnPath, initTab: tabFromPath ?? "building" },
    });
  }, [prevLocation, navigate, detailReturnPath, tabFromPath, routerLoc.state]);

  const goToNextLease = useCallback(async () => {
    if (!nextLease) return;
    const proceed = await locationDetailRef.current?.onBeforeNavigate?.("next", nextLease.city);
    if (proceed === false) return;
    saveCurrentNavLease(nextLease.id);
    saveCurrentNavLeaseLocation(nextLease.locationId);
    setSelectedLeaseId(nextLease.id);
    navigate(buildLocationUrl(nextLease.locationId, "lease"), {
      state: { returnPath: "/leases", initTab: "lease", leaseId: nextLease.id },
    });
  }, [nextLease, navigate]);

  const goToPrevLease = useCallback(async () => {
    if (!prevLease) return;
    const proceed = await locationDetailRef.current?.onBeforeNavigate?.("prev", prevLease.city);
    if (proceed === false) return;
    saveCurrentNavLease(prevLease.id);
    saveCurrentNavLeaseLocation(prevLease.locationId);
    setSelectedLeaseId(prevLease.id);
    navigate(buildLocationUrl(prevLease.locationId, "lease"), {
      state: { returnPath: "/leases", initTab: "lease", leaseId: prevLease.id },
    });
  }, [prevLease, navigate]);

  const handleAddLocationCreated = useCallback((newLocation) => {
    log.ui("AppShell: location created - bumping portfolio refresh key", { id: newLocation?.id });
    setPortfolioRefreshKey((k) => k + 1);
  }, []);

  const handleOpenMarketSetup = useCallback((market) => {
    try {
      window.localStorage.setItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY, "markets_location_ids");
    } catch {
      // AdminPage still defaults safely if private browsing blocks storage.
    }
    navigate("/admin");
  }, [navigate]);

  // ─── Role/view coherence guard ────────────────────────────────────────
  useEffect(() => {
    if (view === VIEW_ADMIN && currentRole && currentRole !== "admin") {
      navigate("/offices", { replace: true });
    }
  }, [view, currentRole, navigate]);

  // ─── Feature flag view guard ──────────────────────────────────────────
  useEffect(() => {
    // Do not guard a deep link against the lean boot fallback. The persisted
    // report governance may enable a report that is intentionally absent from
    // that fallback; wait until the public flag request has settled.
    if (!authChecked || !flagsReady) return;
    if (view === VIEW_DATA_HEALTH) {
      try { localStorage.setItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY, "data"); } catch (_) {}
    }
    if (view === VIEW_LEASES && !sensitivity.canSee("leases.page")) {
      navigate(safeEnabledNavPath(flags, nav_config, currentRole, sensitivity.canSee), { replace: true });
      return;
    }
    const governedKey = governedKeyForView(view);
    if (governedKey && !isGovernedNavEnabled(flags, nav_config, governedKey)) {
      navigate(safeEnabledNavPath(flags, nav_config, currentRole, sensitivity.canSee), { replace: true });
      return;
    }
    // The view guard also handles non-nav gates (individual reports, Decisions).
    const safe = applyViewGuard(view, flags, reportFlags, nav_config);
    if (safe !== view) {
      navigate(viewToPath(safe), { replace: true });
    }
  }, [view, flags, reportFlags, flagsReady, authChecked, navigate, nav_config, currentRole, sensitivity]);

  // ─── Block render until auth settles ─────────────────────────────────
  if (!authChecked) return null;

  // ─── Derived shell values ─────────────────────────────────────────────
  const isLocationDetail = view === VIEW_LOCATION_DETAIL;
  // The dedicated project page owns its own header; suppress the generic
  // portfolio title/subtitle while keeping Projects as the active nav item.
  const isProjectFullPage = view === VIEW_PROJECTS && Boolean(projectIdFromPath);
  const isMarketRecord = /^\/markets\/(?!plan\/?$|strategy\/?$|pressure\/?$|scenario\/?$|guidance\/?$)[^/]+(?:\/[^/]+)?\/?$/.test(pathname);
  const isLeaseDetail    = pathname.startsWith("/leases/") && !isLeasesLandingPath(pathname);
  const isContainedDetail = isLocationDetail || isLeaseDetail || isMarketRecord || isProjectFullPage;
  const isImportSession  = view === VIEW_IMPORTS_SESSION;
  const isProjectDetail  = view === VIEW_PROJECT_DETAIL;
  const isReportFullBleed = REPORT_DETAIL_VIEW_KEYS.has(view);

  const coverageVisible = dataCoverageAllowed
    && isGovernedNavEnabled(flags, nav_config, "reports")
    && reportFlags.some((r) => r.key === VIEW_REPORT_DATA_COVERAGE && r.enabled === true);
  const reportMetadata = (view !== VIEW_REPORT_DATA_COVERAGE || coverageVisible)
    ? REPORT_METADATA[view] ?? null : null;
  const pageLabel = pathname.startsWith("/places") ? "Places" : reportMetadata?.title ?? (isPortfolioCommand
    ? "Portfolio Command"
    : isLocationDetail
    ? derivePageLabel({ view: VIEW_LOCATION_DETAIL, selectedLocationId: locationIdFromPath, portfolioLocations })
    : (PAGE_LABELS_MAP[view] ?? ""));

  const _selectedLocForSubtitle = (isLocationDetail && locationIdFromPath)
    ? (portfolioLocations || []).find((l) => l.id === locationIdFromPath) ?? null
    : null;
  const locationSubtitle = _selectedLocForSubtitle ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: T.fwSemibold }}>
        {_selectedLocForSubtitle.id}
      </span>
      {STAGES[_selectedLocForSubtitle.stage]?.label && (
        <>
          <span style={{ color: T.light }}>·</span>
          <StageBadge stage={_selectedLocForSubtitle.stage} canWrite={false} />
        </>
      )}
      {_selectedLocForSubtitle.isHub && (
        <>
          <span style={{ color: T.light }}>·</span>
          <span style={{ color: T.dark, fontWeight: T.fwSemibold }}>◆ Hub</span>
        </>
      )}
    </span>
  ) : null;

  const detailUniversalSearchPalette = (
    <UniversalSearchPalette
      open={universalSearchOpen}
      onClose={closeUniversalSearch}
      onNavigate={handleUniversalSearchNavigate}
      onAction={handleUniversalSearchAction}
      onAsk={flags.FEATURE_ASK_PORTFOLIO_NAV ? openAskFromSearch : undefined}
      onQueryChange={setUniversalSearchQuery}
      query={universalSearchQuery}
      inputRef={universalSearchInputRef}
      categoryVisibility={universalSearchCategoryVisibility}
      availableReportKeys={universalSearchReportKeys}
      anchored
    />
  );

  const locationHeaderRight = (
    <TopBarControls
      currentUser={currentUser}
      currentRole={currentRole}
      flags={flags}
      onRoleChange={handleDemoRoleChange}
      showSearch={false}
      showSeparator
    />
  );
  const detailHeaderSearch = (
    <CenteredSearchAnchor
      onOpenSearch={openUniversalSearch}
      onCloseSearch={closeUniversalSearch}
      onClearSearch={clearUniversalSearch}
      onQueryChange={setUniversalSearchQuery}
      searchInputRef={universalSearchInputRef}
      searchOpen={universalSearchOpen}
      searchQuery={universalSearchQuery}
      searchPalette={detailUniversalSearchPalette}
    />
  );

  // Sub-nav band slots
  const subNavBackStyle = {
    background: "none", border: "none", cursor: "pointer", padding: 0,
    display: "inline-flex", alignItems: "center", gap: 5,
    color: "var(--v2-accent)", fontSize: T.fsSm, fontWeight: T.fwSemibold,
  };

  let shellSubNavLeft = null;
  if (view === VIEW_PROJECTS && !isProjectFullPage) {
    shellSubNavLeft = <ProjectsNavigationHost />;
  } else if (view === VIEW_LEASES && !isLeaseDetail) {
    shellSubNavLeft = <LeasesSubNav />;
  } else if (isImportSession) {
    shellSubNavLeft = (
      <button data-testid="v2-topbar-back" onClick={backFromImports} style={subNavBackStyle}>
        ‹ Pending Imports
      </button>
    );
  } else if (isProjectDetail) {
    shellSubNavLeft = (
      <button data-testid="v2-topbar-back" onClick={backFromProjectDetail} style={subNavBackStyle}>
        ‹ Projects
      </button>
    );
  }

  const currentNavView = pathToNavView(pathname);

  // ─── Sub-nav band style override ─────────────────────────────────────
  // InFocus Radar uses a white-card (var(--v2-card)) background to match the
  // Offices detail 2nd-row back-nav pattern; all other views keep the default
  // tinted canvas (SUBNAV_BACKGROUND).
  const shellSubNavStyle = view === VIEW_INFOCUS_RADAR
    ? { background: "var(--v2-card)", borderBottom: "1px solid var(--v2-line)" }
    : null;

  // ─── App content ──────────────────────────────────────────────────────
  // Places owns navigation inside main, so its normal-flow content owns the
  // entry gap. Shell-hosted collection nav uses main's gap below navigation.
  const isPlacesCollection = /^\/places\/(offices|markets|capacity)\/?$/.test(pathname);
  const oneRow = isPlacesCollection || view === VIEW_PULSE || view === VIEW_REPORTS
    || REPORT_DETAIL_VIEW_KEYS.has(view) || (view === VIEW_PROJECTS && !isProjectFullPage)
    || (view === VIEW_LEASES && !isLeaseDetail);
  const sectionLabel = isPlacesCollection ? "Places" : REPORT_DETAIL_VIEW_KEYS.has(view) ? "Reports"
    : view === VIEW_PROJECTS ? "Projects" : view === VIEW_LEASES ? "Leases" : pageLabel;
  const mainPaddingTop = (isContainedDetail || isReportFullBleed || isPlacesCollection) ? 0 : "var(--space-page-y)";
  const recordChrome = { headerSearch: detailHeaderSearch, headerRightSlot: locationHeaderRight, searchOpen: universalSearchOpen };
  const marketPlanRouteProps = {
    planData,
    currentRole: currentRole || "viewer",
    deepLinkLocationId: planDeepLinkLocationId,
    deepLinkMarketId: planDeepLinkMarketId,
    onDeepLinkHandled: () => { setPlanDeepLinkLocationId(null); setPlanDeepLinkMarketId(null); },
    onPlanRefreshed: (next) => setPlanData(next),
    onStartWorkflow: isGovernedNavEnabled(flags, nav_config, "workflows") ? startWorkflowFrom : undefined,
    onOpenLinkedRun: isGovernedNavEnabled(flags, nav_config, "workflows") ? openLinkedWorkflowRun : undefined,
    onOpenLocation: (locationId) => {
      setNavContext("locations");
      navigate(buildLocationUrl(locationId), {
        state: { returnPath: buildMarketPlanUrl("plan"), initTab: "building" },
      });
    },
  };
  return (
    <CollectionChromeProvider reportChrome={REPORT_DETAIL_VIEW_KEYS.has(view)}>
    <ProjectsChromeProvider>
    <KpiBandProvider userId={currentUser?.id}>
    <SensitivityPolicyProvider
      role={currentRole}
      taxonomy={currentUser?.sensitivityPolicy}
      ready={authChecked}
    >
    <ShellV2
      oneRow={oneRow}
      sectionLabel={sectionLabel}
      headerLeft={isPlacesCollection ? <CollectionNavigationHost /> : REPORT_DETAIL_VIEW_KEYS.has(view)
        ? <button className="one-row-back-link one-row-report-back" data-testid="report-back-button" onClick={() => navigate("/reports")}>‹ Reports</button> : null}
      headerActions={REPORT_DETAIL_VIEW_KEYS.has(view) ? <div className="one-row-report-pager object-record-actions"><LocationNavPair
        prevLabel={prevReport?.title} nextLabel={nextReport?.title}
        onPrev={prevReport ? () => navigate(viewToPath(prevReport.key)) : undefined}
        onNext={nextReport ? () => navigate(viewToPath(nextReport.key)) : undefined}
      /></div> : null}
      autoCollapseNavigation={Boolean(projectIdFromPath)}
      activeView={currentNavView}
      currentUser={currentUser}
      currentRole={currentRole}
      actualRole={actualRole}
      onNavigate={goToView}
      onRoleChange={handleDemoRoleChange}
      onExitRolePreview={handleExitRolePreview}
      onOpenSearch={openUniversalSearch}
      onCloseSearch={closeUniversalSearch}
      onClearSearch={clearUniversalSearch}
      onQueryChange={setUniversalSearchQuery}
      searchInputRef={universalSearchInputRef}
      searchOpen={universalSearchOpen}
      searchQuery={universalSearchQuery}
      searchPalette={
        <UniversalSearchPalette
          open={universalSearchOpen}
          onClose={closeUniversalSearch}
          onNavigate={handleUniversalSearchNavigate}
          onAction={handleUniversalSearchAction}
          onAsk={flags.FEATURE_ASK_PORTFOLIO_NAV ? openAskFromSearch : undefined}
          onQueryChange={setUniversalSearchQuery}
          query={universalSearchQuery}
          inputRef={universalSearchInputRef}
          categoryVisibility={universalSearchCategoryVisibility}
          availableReportKeys={universalSearchReportKeys}
          anchored
        />
      }
      pageLabel={(isLocationDetail || isLeaseDetail || isProjectFullPage || (oneRow && !REPORT_DETAIL_VIEW_KEYS.has(view))) ? null : pageLabel}
      pageSubtitle={
        pathname.startsWith("/places") ? "Every location in the portfolio - offices, markets, capacity & planning" : reportMetadata?.subtitle ?? (
          (isLocationDetail || isLeaseDetail || isProjectFullPage) ? null :
          (view === VIEW_LEASES && leasesSubtitle) ? leasesSubtitle :
           (PAGE_SUBTITLES[view] ?? null)
        )
      }
      pageStatus={isPortfolioCommand ? <PortfolioCommandHeaderStatus indexData={portfolioCommandIndex} /> : null}
      hideTopBar={isContainedDetail}
      contentOwnsScroll={!isContainedDetail}
      subNavLeft={shellSubNavLeft}
      subNavRight={null}
      subNavStyle={shellSubNavStyle}
      collectionSubNav={(view === VIEW_PROJECTS && !isProjectFullPage) || (view === VIEW_LEASES && !isLeaseDetail)}
      onKeyboardPrev={isLocationDetail
        ? (navContext === "leases" ? (prevLease ? goToPrevLease : null) : (prevLocation ? goToPrevLocation : null))
        : null}
      onKeyboardNext={isLocationDetail
        ? (navContext === "leases" ? (nextLease ? goToNextLease : null) : (nextLocation ? goToNextLocation : null))
        : null}
    >
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, height: "100%", overflow: "visible" }}>
        <AskContext.Provider value={askContextValue}>
          {probeFailed && (
            <div
              role="status"
              data-testid="probe-failed-banner"
              style={{
                background: T.utilAmber, borderBottom: `1px solid ${T.amber}`,
                color: T.dark, padding: "8px 16px", fontSize: T.fsBase,
                display: "flex", alignItems: "center", gap: 12,
              }}
            >
              <span style={{ flex: 1 }}>
                Could not load user profile - some features may be hidden.
              </span>
              <button
                type="button"
                data-testid="probe-retry-button"
                onClick={onManualRetry}
                style={{
                  background: T.white, border: `1px solid ${T.amber}`,
                  color: T.dark, padding: "3px 10px", borderRadius: 5,
                  fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: "pointer",
                }}
              >
                Retry
              </button>
            </div>
          )}

          <main
            tabIndex={-1}
            style={{
              flex: 1, minWidth: 0, minHeight: isContainedDetail ? 0 : undefined, overflowY: isContainedDetail ? "hidden" : "visible",
              paddingLeft: 0, paddingRight: 0,
              paddingTop: mainPaddingTop,
              paddingBottom: isContainedDetail ? 0 : "var(--space-page-y)",
            }}
          >
            <Routes>
              {/* Default landing — handles deep-link params and defaultView redirect */}
              <Route
                path="/"
                element={
                  <DefaultLandingRoute
                    currentUser={currentUser}
                    flags={flags}
                    navConfig={nav_config}
                    navContext={navContext}
                  />
                }
              />

              {/* ── Pulse redirect (/pulse → /home) ───────────────── */}
              <Route path="/pulse" element={<PulseRedirect />} />

              {/* ── Home (was /pulse) ──────────────────────────────── */}
              <Route
                path="/home"
                element={
                  <PulsePageWithView
                    currentRole={currentRole || "viewer"}
                    onStartWorkflow={isGovernedNavEnabled(flags, nav_config, "workflows") ? startWorkflowFrom : undefined}
                    onOpenLinkedRun={isGovernedNavEnabled(flags, nav_config, "workflows") ? openLinkedWorkflowRun : undefined}
                    focusDecisionLocationId={decisionFocusLocationId}
                    onDecisionFocusHandled={() => setDecisionFocusLocationId(null)}
                    onNavigate={(urlString) => {
                      const params = new URLSearchParams(urlString.replace(/^\?/, ""));
                      const v = params.get("view");
                      if (v === "leases") {
                         navigate(homeLeaseNavigationPath(urlString));
                      }
                      else if (v === "infocus-radar") navigate("/infocus-radar");
                      else if (v === "admin") {
                        const section = params.get("section");
                        const tab = params.get("tab");
                        const adminParams = new URLSearchParams();
                        if (section) adminParams.set("section", section);
                        if (tab) adminParams.set("tab", tab);
                        navigate(adminParams.size > 0 ? `/admin?${adminParams.toString()}` : "/admin");
                      }
                      else if (v === "whatif") navigate(buildMarketPlanUrl("whatif"));
                      else if (v === "datatrust")      navigate("/home?view=datatrust");
                      else if (v === "plan-coverage")  navigate("/home?view=plan-coverage");
                      else if (v === "review-cadence") navigate("/home?view=review-cadence");
                      else if (v === "pulse" || v === "portfolio-command") navigate("/home");
                      else if (v === "reviews") {
                        const itemRef = params.get("itemRef") || null;
                        navigate(itemRef ? `/reviews?itemRef=${encodeURIComponent(itemRef)}` : "/reviews");
                      }
                      else if (v === "plan") {
                        const locationId = params.get("location")  || null;
                        const marketId   = params.get("marketId") || null;
                        hasNavigated.current = true;
                        setPlanDeepLinkLocationId(locationId);
                        setPlanDeepLinkMarketId(marketId);
                        navigate(buildMarketPlanUrl("plan"));
                      }
                    }}
                  />
                }
              />

              {/* ── Offices ───────────────────────────────────────── */}
              <Route
                path="/places/offices"
                element={
                  <PlacesFrame lens="offices"
                    fallbackPath={safeEnabledNavPath(flags, nav_config?.filter((row) => row.key !== "places") ?? [], currentRole, sensitivity.canSee)}
                  >
                    <PortfolioView
                      onSelectLocation={openLocationFromPortfolio}
                      currentRole={currentRole || "viewer"}
                      refreshKey={portfolioRefreshKey}
                      planData={planData}
                      onAddLocation={
                        currentRole === "admin" || currentRole === "editor"
                          ? () => setAddLocationModalOpen(true)
                          : undefined
                      }
                      search={search}
                      setSearch={setSearch}
                      onNavListChange={setOfficesNavIds}
                    />
                  </PlacesFrame>
                }
              />
              <Route path="/offices" element={<PlacesAlias lens="offices" />} />
              <Route path="/places" element={<PlacesAlias lens={resolvePlacesLens(null, placesTabs) ?? "offices"} />} />
              <Route path="/places/markets" element={
                <PlacesFrame lens="markets"
                  fallbackPath={safeEnabledNavPath(flags, nav_config?.filter((row) => row.key !== "places") ?? [], currentRole, sensitivity.canSee)}>
                  <MarketPlanPage {...marketPlanRouteProps} placesLens="markets" />
                </PlacesFrame>
              } />
              <Route path="/places/capacity" element={
                <PlacesFrame lens="capacity"
                  fallbackPath={safeEnabledNavPath(flags, nav_config?.filter((row) => row.key !== "places") ?? [], currentRole, sensitivity.canSee)}>
                  <CapacityPage key={currentUser?.id ?? "anonymous"} userId={currentUser?.id} currentRole={currentRole || "viewer"} />
                </PlacesFrame>
              } />
              {/* PLACES-CAPACITY-UNIFIED: retired collection lenses redirect; components kept for rollback. */}
              <Route path="/places/pressure" element={<CapacityRedirect fromLens="pressure" />} />
              <Route path="/places/scenario" element={<CapacityRedirect fromLens="scenario" />} />

              {/* ── Location detail (with optional :tab) ─────────── */}
              <Route
                path="/offices/:locationId"
                element={
                  <GovernedDestination
                    enabled={isGovernedNavEnabled(flags, nav_config, "portfolio")}
                    flags={flags}
                    navConfig={nav_config}
                    currentRole={currentRole}
                  >
                    <LocationDetailWrapper
                    locationDetailRef={locationDetailRef}
                    locationId={locationIdFromPath}
                    initTab={detailInitTab}
                    backLabel={backLabel}
                    onBack={backFromDetail}
                    onArchived={handleOfficeArchived}
                    currentRole={currentRole}
                    navContext={navContext}
                    selectedLeaseId={selectedLeaseId}
                    nextLocation={nextLocation}
                    prevLocation={prevLocation}
                    nextLease={nextLease}
                    prevLease={prevLease}
                    goToNextLocation={goToNextLocation}
                    goToPrevLocation={goToPrevLocation}
                    goToNextLease={goToNextLease}
                    goToPrevLease={goToPrevLease}
                    portfolioLocations={portfolioLocations}
                    locationHeaderRight={locationHeaderRight}
                    headerSearch={detailHeaderSearch}
                    searchOpen={universalSearchOpen}
                    search={search}
                    flags={flags}
                    onOpenAsk={(scope) => { setAskScope(scope); setAskOpen(true); }}
                    onLeaseEditSaved={(updatedLease) => {
                      if (!updatedLease) return;
                      patchPortfolioLeasesCache(updatedLease);
                      setAllLeases((prev) => prev.map((l) =>
                        l.id === updatedLease.id ? { ...l, ...updatedLease } : l
                      ));
                    }}
                    onLinkBudget={
                      currentRole === "admin" || currentRole === "editor"
                        ? () => navigate("/admin")
                        : undefined
                    }
                    onPlannedSeatsSaved={refreshPlanData}
                    onCapacityScheduleSaved={() => {
                      setPortfolioRefreshKey((k) => k + 1);
                      refreshPlanData();
                    }}
                    onHeadcountScheduleSaved={() => {
                      setPortfolioRefreshKey((k) => k + 1);
                      refreshPlanData();
                    }}
                    currentUser={currentUser}
                    />
                  </GovernedDestination>
                }
              />
              <Route
                path="/offices/:locationId/:tab"
                element={
                  <GovernedDestination
                    enabled={isGovernedNavEnabled(flags, nav_config, "portfolio")}
                    flags={flags}
                    navConfig={nav_config}
                    currentRole={currentRole}
                  >
                    <LocationDetailWrapper
                    locationDetailRef={locationDetailRef}
                    locationId={locationIdFromPath}
                    initTab={detailInitTab}
                    backLabel={backLabel}
                    onBack={backFromDetail}
                    onArchived={handleOfficeArchived}
                    currentRole={currentRole}
                    navContext={navContext}
                    selectedLeaseId={selectedLeaseId}
                    nextLocation={nextLocation}
                    prevLocation={prevLocation}
                    nextLease={nextLease}
                    prevLease={prevLease}
                    goToNextLocation={goToNextLocation}
                    goToPrevLocation={goToPrevLocation}
                    goToNextLease={goToNextLease}
                    goToPrevLease={goToPrevLease}
                    portfolioLocations={portfolioLocations}
                    locationHeaderRight={locationHeaderRight}
                    headerSearch={detailHeaderSearch}
                    searchOpen={universalSearchOpen}
                    search={search}
                    flags={flags}
                    onOpenAsk={(scope) => { setAskScope(scope); setAskOpen(true); }}
                    onLeaseEditSaved={(updatedLease) => {
                      if (!updatedLease) return;
                      patchPortfolioLeasesCache(updatedLease);
                      setAllLeases((prev) => prev.map((l) =>
                        l.id === updatedLease.id ? { ...l, ...updatedLease } : l
                      ));
                    }}
                    onLinkBudget={
                      currentRole === "admin" || currentRole === "editor"
                        ? () => navigate("/admin")
                        : undefined
                    }
                    onPlannedSeatsSaved={refreshPlanData}
                    onCapacityScheduleSaved={() => {
                      setPortfolioRefreshKey((k) => k + 1);
                      refreshPlanData();
                    }}
                    onHeadcountScheduleSaved={() => {
                      setPortfolioRefreshKey((k) => k + 1);
                      refreshPlanData();
                    }}
                    currentUser={currentUser}
                    />
                  </GovernedDestination>
                }
              />

              {/* ── Leases ────────────────────────────────────────── */}
              {["/leases", "/leases/timeline"].map((path) => <Route
                key={path}
                path={path}
                element={
                  <GovernedDestination
                    enabled={sensitivity.canSee("leases.page")}
                    flags={flags}
                    navConfig={nav_config}
                    currentRole={currentRole}
                    canSee={sensitivity.canSee}
                  >
                  <LeasesPage
                    onSelectLocation={openLocationFromLeasesNav}
                    currentUser={currentUser}
                    currentRole={currentRole || "viewer"}
                    onOpenImportsQueue={openImportsQueue}
                    onGoToPlan={() => navigate(buildMarketPlanUrl("plan"))}
                    onSubtitleChange={setLeasesSubtitle}
                    onNavListChange={setLeasesNavIds}
                  />
                  </GovernedDestination>
                }
              />)}
              <Route
                path="/leases/:leaseId"
                element={
                  <GovernedDestination enabled={sensitivity.canSee("leases.page")} flags={flags} navConfig={nav_config} currentRole={currentRole} canSee={sensitivity.canSee}>
                    <LeaseDetailPage
                      currentRole={currentRole}
                      currentUser={currentUser}
                      flags={flags}
                      onRoleChange={handleDemoRoleChange}
                      onOpenSearch={openUniversalSearch}
                      onCloseSearch={closeUniversalSearch}
                      onClearSearch={clearUniversalSearch}
                      onQueryChange={setUniversalSearchQuery}
                      searchInputRef={universalSearchInputRef}
                      searchOpen={universalSearchOpen}
                      searchQuery={universalSearchQuery}
                      searchPalette={detailUniversalSearchPalette}
                      onSubtitleChange={setLeasesSubtitle}
                    />
                  </GovernedDestination>
                }
              />
              <Route
                path="/leases/:leaseId/:tab"
                element={
                  <GovernedDestination enabled={sensitivity.canSee("leases.page")} flags={flags} navConfig={nav_config} currentRole={currentRole} canSee={sensitivity.canSee}>
                    <LeaseDetailPage
                      currentRole={currentRole}
                      currentUser={currentUser}
                      flags={flags}
                      onRoleChange={handleDemoRoleChange}
                      onOpenSearch={openUniversalSearch}
                      onCloseSearch={closeUniversalSearch}
                      onClearSearch={clearUniversalSearch}
                      onQueryChange={setUniversalSearchQuery}
                      searchInputRef={universalSearchInputRef}
                      searchOpen={universalSearchOpen}
                      searchQuery={universalSearchQuery}
                      searchPalette={detailUniversalSearchPalette}
                      onSubtitleChange={setLeasesSubtitle}
                    />
                  </GovernedDestination>
                }
              />

              {/* ── Legacy Decisions URL ─────────────────────────── */}
              <Route path="/decisions" element={<Navigate to="/radar/decisions" replace />} />

              {/* ── Reviews Register ──────────────────────────────── */}
              {/* P-19: portfolio reviews check-off register. */}
              <Route
                path="/reviews"
                element={<ReviewsRegisterPage currentRole={currentRole} />}
              />

              {/* ── Markets ────────────────────────────────────────── */}
              {/* Bare /markets always means the Markets list. */}
              <Route
                path="/markets"
                element={<PlacesAlias lens="markets" />}
              />
              {/* Retain the explicit legacy Plan bookmark as an alias. */}
              <Route path="/markets/plan" element={<PlacesAlias lens="markets" />} />
              {/* Static tab routes must precede the dynamic market detail route. */}
              <Route path="/markets/strategy" element={
                <GovernedDestination enabled={isGovernedNavEnabled(flags, nav_config, "plan")} flags={flags} navConfig={nav_config} currentRole={currentRole}>
                  <MarketPlanPage {...marketPlanRouteProps} />
                </GovernedDestination>
              } />
              <Route path="/markets/pressure" element={<CapacityRedirect fromLens="pressure" />} />
              <Route path="/markets/scenario" element={<CapacityRedirect fromLens="scenario" />} />
              <Route path="/markets/guidance" element={
                <GovernedDestination enabled={isGovernedNavEnabled(flags, nav_config, "plan")} flags={flags} navConfig={nav_config} currentRole={currentRole}>
                  <MarketPlanPage {...marketPlanRouteProps} />
                </GovernedDestination>
              } />
              <Route path="/markets/:marketId" element={
                  <GovernedDestination enabled={isGovernedNavEnabled(flags, nav_config, "plan")} flags={flags} navConfig={nav_config} currentRole={currentRole}>
                    <MarketRecordRedirect />
                  </GovernedDestination>
              } />
              {/* PLACES-SLICE-2: market record tabs (offices | pressure | scenario). */}
              <Route path="/markets/:marketId/:recordTab" element={
                  <GovernedDestination enabled={isGovernedNavEnabled(flags, nav_config, "plan")} flags={flags} navConfig={nav_config} currentRole={currentRole}>
                    <MarketRecordRedirect />
                  </GovernedDestination>
              } />
              <Route
                path="/markets/*"
                element={
                  <GovernedDestination enabled={isGovernedNavEnabled(flags, nav_config, "plan")} flags={flags} navConfig={nav_config} currentRole={currentRole}>
                    <MarketPlanPage {...marketPlanRouteProps} />
                  </GovernedDestination>
                }
              />
              {/* Explicit legacy redirects preserve old bookmarks and queries. */}
              <Route path="/market-plan" element={<LegacyMarketPlanRedirect />} />
              <Route path="/market-plan/*" element={<LegacyMarketPlanRedirect />} />

              {/* ── Projects ──────────────────────────────────────── */}
              {[null, ...PROJECT_TAB_SEGMENTS].map((tab) => <Route
                key={tab || "projects"}
                path={tab ? `/projects/${tab}` : "/projects"}
                element={
                  <ProjectsPage
                    currentRole={currentRole || "viewer"}
                    authIdentity={JSON.stringify([currentUser?.id, currentUser?.devActAs, currentRole])}
                    locationIdFilter={projectsLocationId}
                    onClearLocationFilter={() => {
                      const params = new URLSearchParams(routerLoc.search);
                      params.delete("locationId");
                      navigate(`${routerLoc.pathname}${params.size ? `?${params}` : ""}`, { replace: true });
                    }}
                    onExpandedProjectChange={(id) => navigate(
                      id ? buildProjectsNavigationUrl(id, projectsLocationId) : `${routerLoc.pathname}${routerLoc.search}`,
                    )}
                  />
                }
              />)}
              <Route
                path="/projects/:projectId"
                element={
                  <Navigate to={buildProjectUrl(projectIdFromPath, routerLoc.search)} replace />
                }
              />

              {/* ── Reports ───────────────────────────────────────── */}
              <Route
                path="/reports/data-coverage"
                element={!authChecked || !flagsReady ? null
                  : !dataCoverageAllowed ? <Navigate to="/reports" replace />
                  : !isGovernedNavEnabled(flags, nav_config, "reports")
                    ? <Navigate to={safeEnabledNavPath(flags, nav_config, currentRole, sensitivity.canSee)} replace />
                  : !reportFlags.some((r) => r.key === VIEW_REPORT_DATA_COVERAGE && r.enabled === true)
                    ? <Navigate to="/reports" replace />
                  : <DataCoverageReport
                    currentRole={currentRole}
                    onBack={() => navigate("/reports")}
                    prevReport={prevReport}
                    nextReport={nextReport}
                    onNavigateReport={onNavigateReport}
                  />}
              />
              <Route
                path="/reports"
                element={
                  <ReportErrorBoundary>
                    <ReportsPage
                      currentRole={currentRole}
                      onSelectReport={(key) => navigate(viewToPath(key))}
                      onNavListChange={setReportNavList}
                    />
                  </ReportErrorBoundary>
                }
              />
              <Route
                path="/reports/wpr"
                element={
                  <ReportErrorBoundary>
                    <WeeklyPortfolioReview
                      onBack={() => navigate("/reports")}
                      currentRole={currentRole || "viewer"}
                      prevReport={prevReport}
                      nextReport={nextReport}
                      onNavigateReport={onNavigateReport}
                    />
                  </ReportErrorBoundary>
                }
              />
              <Route
                path="/reports/headcount-trend"
                element={
                  <ReportErrorBoundary>
                    <HeadcountTrendReport
                      onBack={() => navigate("/reports")}
                      onOpenLocation={openLocationFromReport(VIEW_REPORT_HC_TREND)}
                      prevReport={prevReport}
                      nextReport={nextReport}
                      onNavigateReport={onNavigateReport}
                    />
                  </ReportErrorBoundary>
                }
              />
              <Route
                path="/reports/open-roles"
                element={
                  <ReportErrorBoundary>
                    <OpenRolesReport
                      onBack={() => navigate("/reports")}
                      prevReport={prevReport}
                      nextReport={nextReport}
                      onNavigateReport={onNavigateReport}
                    />
                  </ReportErrorBoundary>
                }
              />
              <Route
                path="/reports/space-fit"
                element={
                  <ReportErrorBoundary>
                    <SpaceFitReport
                      onBack={() => navigate("/reports")}
                      prevReport={prevReport}
                      nextReport={nextReport}
                      onNavigateReport={onNavigateReport}
                    />
                  </ReportErrorBoundary>
                }
              />
              <Route
                path="/reports/cost-leakage"
                element={
                  <ReportErrorBoundary>
                    <CostLeakageReport
                      onBack={() => navigate("/reports")}
                      prevReport={prevReport}
                      nextReport={nextReport}
                      onNavigateReport={onNavigateReport}
                    />
                  </ReportErrorBoundary>
                }
              />
              <Route
                path="/reports/rto-compliance"
                element={
                  <ReportErrorBoundary>
                    <RtoComplianceReport
                      onBack={() => navigate("/reports")}
                      prevReport={prevReport}
                      nextReport={nextReport}
                      onNavigateReport={onNavigateReport}
                    />
                  </ReportErrorBoundary>
                }
              />
              <Route
                path="/reports/foot-traffic"
                element={
                  <ReportErrorBoundary>
                    <FootTrafficReport
                      onBack={() => navigate("/reports")}
                      prevReport={prevReport}
                      nextReport={nextReport}
                      onNavigateReport={onNavigateReport}
                    />
                  </ReportErrorBoundary>
                }
              />
              <Route
                path="/reports/utilization"
                element={
                  <ReportErrorBoundary>
                    <UtilizationReport
                      onBack={() => navigate("/reports")}
                      prevReport={prevReport}
                      nextReport={nextReport}
                      onNavigateReport={onNavigateReport}
                    />
                  </ReportErrorBoundary>
                }
              />
              <Route path="/reports/lease-countdown"    element={<Navigate to="/reports" replace />} />
              <Route path="/reports/office-capacity"    element={<Navigate to="/reports" replace />} />
              <Route path="/reports/office-utilization" element={<Navigate to="/reports" replace />} />

              {/* ── Workflow run detail ───────────────────────────── */}
              <Route
                path="/workflows/runs/:id"
                element={
                  isGovernedNavEnabled(flags, nav_config, "workflows") ? (
                    <ClosureRunPage currentRole={currentRole || "viewer"} />
                  ) : <Navigate to="/offices" replace />
                }
              />

              {/* ── Workflows ─────────────────────────────────────── */}
              <Route
                path="/workflows"
                element={
                  isGovernedNavEnabled(flags, nav_config, "workflows") ? (
                    <WorkflowsPage
                      currentRole={currentRole || "viewer"}
                      currentUserId={currentUser?.id ?? null}
                      launchSource={workflowLaunchSource}
                      onLaunchSourceConsumed={() => setWorkflowLaunchSource(null)}
                      openRunId={openWorkflowRunId}
                      onOpenRunIdConsumed={() => setOpenWorkflowRunId(null)}
                      onNavigateToSource={navigateFromWorkflowRun}
                    />
                  ) : <Navigate to="/offices" replace />
                }
              />

              {/* ── Roadmap ───────────────────────────────────────── */}
              <Route
                path="/roadmap"
                element={
                  isGovernedNavEnabled(flags, nav_config, "roadmap") ? <RoadmapPage /> : <Navigate to={safeEnabledNavPath(flags, nav_config, currentRole, sensitivity.canSee)} replace />
                }
              />

              {/* ── Radar ─────────────────────────────────────────── */}
              <Route
                path="/radar/decisions"
                element={
                  flagsReady ? (
                    <GovernedDestination
                      enabled={isGovernedNavEnabled(flags, nav_config, "radar")
                        && flags?.[RADAR_DECISIONS_FLAG] !== false}
                      flags={flags}
                      navConfig={nav_config}
                      currentRole={currentRole}
                      canSee={sensitivity.canSee}
                    >
                      <RadarDecisionsPanel currentRole={currentRole} focused />
                    </GovernedDestination>
                  ) : null
                }
              />
              {/* RADAR-PHASE-1-SPINE: When FEATURE_RADAR_SPINE is on, render the
                  new three-band RadarSpinePage; when off, show the existing
                  brief-stack RadarPage unchanged. */}
              <Route
                path="/radar"
                element={
                  flagsReady ? (
                    <GovernedDestination
                      enabled={isGovernedNavEnabled(flags, nav_config, "radar")}
                      flags={flags}
                      navConfig={nav_config}
                      currentRole={currentRole}
                      canSee={sensitivity.canSee}
                    >
                      {flags?.[FEATURE_RADAR_SPINE] ? (
                        <RadarSpinePage currentRole={currentRole || "viewer"} />
                      ) : (
                        <RadarPage
                          currentRole={currentRole || "viewer"}
                          onNavigate={(urlString) => {
                            const params = new URLSearchParams(urlString.replace(/^\?/, ""));
                            const v = params.get("view");
                            if (v === "leases") navigate("/leases");
                          }}
                        />
                      )}
                    </GovernedDestination>
                  ) : null
                }
              />

              {/* ── In Focus Radar (now branded "Radar") ─────────── */}
              <Route path="/infocus-radar" element={<InFocusRadarPage currentRole={currentRole || "viewer"} />} />

              {/* ── Playbooks (G-34) ──────────────────────────────── */}
              {/* Sub-routes must be declared before the wildcard /playbooks index */}
              <Route
                path="/playbooks/runs/:runId"
                element={<PlaybookRunPage currentRole={currentRole || "viewer"} />}
              />
              <Route path="/playbooks/runs" element={<PlaybookRunsPage />} />
              <Route
                path="/playbooks"
                element={<PlaybooksPage currentRole={currentRole || "viewer"} />}
              />

              {/* ── Start Here tour ───────────────────────────────── */}
              <Route path="/start-here" element={<StartHerePage displayName={currentUser?.display_name ?? null} />} />

              {/* ── OKR hierarchy ─────────────────────────────────── */}
              <Route
                path="/okrs"
                element={
                  flagsReady ? (
                    <GovernedDestination
                      enabled={isGovernedNavEnabled(flags, nav_config, "okrs")}
                      flags={flags}
                      navConfig={nav_config}
                      currentRole={currentRole}
                      canSee={sensitivity.canSee}
                    >
                      <OkrsPage currentUser={currentUser} currentRole={currentRole || "viewer"} />
                    </GovernedDestination>
                  ) : null
                }
              />

              {/* Universal non-redirecting fallback when every governed
                  destination has been disabled. */}
              <Route path="/nav-unavailable" element={<GovernedNavUnavailable />} />

              {/* ── Admin ─────────────────────────────────────────── */}
              <Route
                path="/admin"
                element={
                  <AdminPage
                    onOpenImportsQueue={openImportsQueue}
                    currentRole={currentRole}
                    onViewDataset={openViewDataset}
                  />
                }
              />
              <Route
                path="/currency-rates"
                element={<CurrencyRatesPage />}
              />
              <Route
                path="/admin/data/imports"
                element={
                  <PendingImportsQueue
                    onOpenSession={openImportsSession}
                    onBack={() => {
                      if (importsQueueReturnSessionId !== null) {
                        const returnId = importsQueueReturnSessionId;
                        setImportsQueueReturnSessionId(null);
                        navigate(buildImportUrl(returnId));
                        return;
                      }
                      try { localStorage.setItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY, "data"); } catch (_) {}
                      navigate("/admin");
                    }}
                  />
                }
              />
              <Route
                path="/admin/data/import/:sessionId"
                element={
                  <ImportSessionReview
                    sessionId={importSessionIdFromPath}
                    onBack={backFromImports}
                    onRestage={() => {
                      try { localStorage.setItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY, "data"); } catch (_) {}
                      navigate("/admin");
                    }}
                  />
                }
              />

              {/* ── Legacy import deep-link ───────────────────────── */}
              <Route path="/imports/:sessionId" element={<LegacyImportRedirect />} />

              {/* ── Catch-all ─────────────────────────────────────── */}
              <Route path="*" element={<Navigate to="/offices" replace />} />
            </Routes>
          </main>
        </AskContext.Provider>
      </div>

      {/* ── Overlays ─────────────────────────────────────────────────── */}
      {flags.FEATURE_ACTIONS_BELL && (
        <ActionsPanel
          open={actionsPanelOpen}
          onClose={() => { setActionsPanelOpen(false); setActionsCountKey((k) => k + 1); }}
          role={currentRole}
          headerHeight={60}
          onNavigate={({ type, id, sourceLocationId, actionId }) => {
            setActionsPanelOpen(false);
            if (type === "location") {
              setNavContext("locations");
              navigate(buildLocationUrl(id), {
                state: { returnPath: "/offices" },
              });
            } else if (type === "lease") {
              navigate(buildLeaseUrl(id));
            }
          }}
        />
      )}

      {flags.FEATURE_ASK_PORTFOLIO_NAV && askOpen && (
        <AskPopout
          key={askSearchDraft ? `search:${askSearchDraft.sequence}` : "context"}
          initialQuery={askSearchDraft?.query ?? ""}
          scope={askScope}
          onScopeChange={(newScope) => setAskScope(newScope)}
          onClose={() => setAskOpen(false)}
          openedViaShortcut={askOpenedViaShortcut}
        />
      )}

      <AddLocationModal
        isOpen={addLocationModalOpen}
        onClose={() => setAddLocationModalOpen(false)}
        onCreated={handleAddLocationCreated}
        onOpenMarketSetup={handleOpenMarketSetup}
      />

      {SHOW_FEEDBACK_WIDGET && (
        <FeedbackWidget currentUser={currentUser} currentRole={currentRole} />
      )}
    </ShellV2>
    </SensitivityPolicyProvider>
    </KpiBandProvider>
    </ProjectsChromeProvider>
    </CollectionChromeProvider>
  );
}

// ─── Route sub-components ────────────────────────────────────────────────────
// Defined outside AppShell to avoid re-creation on every render.

function LocationDetailWrapper({
  locationDetailRef, locationId, initTab, backLabel, onBack, onArchived,
  currentRole, navContext, selectedLeaseId,
  nextLocation, prevLocation, nextLease, prevLease,
  goToNextLocation, goToPrevLocation, goToNextLease, goToPrevLease,
  portfolioLocations, locationHeaderRight, search, flags,
  headerSearch, searchOpen,
  onOpenAsk, onLeaseEditSaved, onLinkBudget,
  onPlannedSeatsSaved, onCapacityScheduleSaved, onHeadcountScheduleSaved,
  currentUser,
}) {
  const routeState = useLocation().state;
  const orderedIds = Array.isArray(routeState?.orderedIds) ? routeState.orderedIds : null;

  if (!locationId) return <Navigate to="/offices" replace />;
  return (
    <LocationDetail
      ref={locationDetailRef}
      locationId={locationId}
      onBack={onBack}
      onArchived={onArchived}
      initialTab={initTab}
      backLabel={backLabel}
      currentRole={currentRole || "viewer"}
      canArchive={currentRole === "admin"}
      showParentTrail={true}
      v2Shell={true}
      headerRightSlot={locationHeaderRight}
      headerSearch={headerSearch}
      searchOpen={searchOpen}
      onNext={navContext === "leases" ? (nextLease ? goToNextLease : undefined) : (nextLocation ? goToNextLocation : undefined)}
      nextLabel={navContext === "leases" ? (nextLease ? nextLease.city : undefined) : (nextLocation ? (nextLocation.locationAlias ?? nextLocation.market) : undefined)}
      onPrev={navContext === "leases" ? (prevLease ? goToPrevLease : undefined) : (prevLocation ? goToPrevLocation : undefined)}
      prevLabel={navContext === "leases" ? (prevLease ? prevLease.city : undefined) : (prevLocation ? (prevLocation.locationAlias ?? prevLocation.market) : undefined)}
      orderedIds={orderedIds}
      portfolioLocations={portfolioLocations}
      navLeaseId={navContext === "leases" ? selectedLeaseId : undefined}
      onAskOpen={flags.FEATURE_ASK_PORTFOLIO_NAV ? onOpenAsk : undefined}
      searchTerm={search}
      onLeaseEditSaved={onLeaseEditSaved}
      onLinkBudget={onLinkBudget}
      onPlannedSeatsSaved={onPlannedSeatsSaved}
      onCapacityScheduleSaved={onCapacityScheduleSaved}
      onHeadcountScheduleSaved={onHeadcountScheduleSaved}
      currentUserName={currentUser?.displayName ?? currentUser?.name ?? ""}
    />
  );
}
